/* =====================================================================
   GCX kit: shared behavior for the personalized-case (gcpx) and
   leather-set (gcls) pages. No dependencies, safe with defer, safe to
   include more than once (second copy exits), and it initializes markup
   that arrives later (section re-renders, theme editor) on its own.

   window.GCX = {
     scrollRows(root)        glass-chevron scroll rows in root (the chevrons glide one item, 1a;
                             a mouse can drag them, 1b; data-gcx-motion="glide": the smooth
                             no-snap row, 1d)
     glideTo(track, x)       glide a kit row to scroll position x, as its chevrons do (1a)
     jump(id)                glide to an element and focus it (in-page jumps without a link, 6b)
     calm()                  pause endless CSS animations while they are off screen (6c, opt-in)
     dragPager(el, opt)      mouse drag that pages a one-picture stage (1c)
     typewriter(input, opt)  typing example initials in a placeholder
     engrave(el, text, anc)  write initials into a .gcx-engrave preview
     setSticky(state)        mirror the page's choice into the PDP stickies
     i18n, t, tpl, num, pct, list, plural, fromPrice, slug, lang, dir
                             language helpers (section 0 below)
     batch(el, low), batchOut(el, o)
                             small-batch pill and sold-out note (section 7b)
     zoom(items, i, opt)     the PDP's full-screen PhotoSwipe (section 9, snippets/gcx-pswp)
     ...helpers documented in design/GCX-KIT.md and i18n/I18N-CONTRACT.md
   }
   Words: the kit has no copy of its own. Every visible or spoken string comes
   from the page (snippet params, data-* attributes, setSticky keys) or from
   the page-wide JSON printed by snippets/gcx-sticky.liquid
   (<script type="application/json" data-gcx-i18n>), which the pages fill from
   translatable section settings and theme locale keys. The English words
   below are last-resort fallbacks only.
   ===================================================================== */
(function () {
  'use strict';
  if (window.GCX && window.GCX.__kit) return;
  var GCX = window.GCX = window.GCX || {};
  GCX.__kit = '1.7.1';

  var doc = document;
  var mqReduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(mqReduce && mqReduce.matches); }
  function toArr(list) { return Array.prototype.slice.call(list || []); }
  /* every element in root (and root itself) matching sel */
  function within(root, sel) {
    root = root || doc;
    var out = root.querySelectorAll ? toArr(root.querySelectorAll(sel)) : [];
    if (root.nodeType === 1 && root.matches && root.matches(sel)) out.unshift(root);
    return out;
  }
  function num(v, d) { var n = parseFloat(v); return isFinite(n) ? n : d; }
  function setText(el, t) { if (el && el.textContent !== t) el.textContent = t; }
  var uid = 0;

  /* ===================================================================
     0. Language helpers
        GCX.i18n            page-wide words (merged from every
                            script[type="application/json"][data-gcx-i18n])
        GCX.t(key, fb)      one word from GCX.i18n, else fb
        GCX.tpl(s, map)     fills [key] and {key} tokens; tokens not in map stay
        GCX.lang()          <html lang>, e.g. "de", "pt-PT", "zh-CN"
        GCX.dir()           "rtl" for ar/he pages (<html dir>), else "ltr"
        GCX.num(n, opt)     Intl number in the page language, Latin digits
        GCX.pct(n, opt)     20 -> "20%" / "20 %" / "%20" by language
        GCX.list(a, type)   ["a","b","c"] -> "a, b and c" by language
        GCX.plural(n, f)    f = {zero?, one, two?, few?, many?, other} -> the form
                            for n (Intl.PluralRules), with [n]/{n} filled
        GCX.fromPrice(p, t) "From [price]" template of the page, price filled
        GCX.priceChip(el, p, isFrom)  update a snippets/gcx-price-chip chip
        GCX.slug(s)         Unicode-aware key ("iPhone 17 Pro" -> "iphone-17-pro");
                            null for an empty result (never match on "")
     =================================================================== */
  var I18N_DEFAULT = { prev: 'Previous', next: 'Next', from: 'From [price]', addToCart: 'Add to Cart', soldOut: 'Sold Out' };
  var i18nCache = null, i18nDone = false;
  function loadI18n() {
    var out = {};
    for (var k in I18N_DEFAULT) out[k] = I18N_DEFAULT[k];
    toArr(doc.querySelectorAll('script[type="application/json"][data-gcx-i18n]')).forEach(function (el) {
      try {
        var o = JSON.parse(el.textContent || '{}');
        for (var key in o) if (Object.prototype.hasOwnProperty.call(o, key) && o[key] != null && o[key] !== '') out[key] = o[key];
      } catch (e) {}
    });
    return out;
  }
  Object.defineProperty(GCX, 'i18n', {
    configurable: true,
    /* re-read until the document is parsed (the JSON is printed near the end) */
    get: function () { if (!i18nCache || !i18nDone) { i18nCache = loadI18n(); i18nDone = doc.readyState !== 'loading'; } return i18nCache; }
  });
  GCX.t = function (key, fb) { var v = GCX.i18n[key]; return v != null && v !== '' ? v : (fb != null ? fb : ''); };
  GCX.tpl = function (str, map) {
    str = str == null ? '' : String(str);
    if (!map) return str;
    return str.replace(/\[([a-z0-9_]+)\]|\{([a-z0-9_]+)\}/gi, function (m, a, b) {
      var k = a || b;
      return Object.prototype.hasOwnProperty.call(map, k) && map[k] != null ? String(map[k]) : m;
    });
  };
  GCX.lang = function () { return (doc.documentElement.getAttribute('lang') || 'en').trim() || 'en'; };
  GCX.dir = function () { return (doc.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl' ? 'rtl' : 'ltr'; };
  /* Latin digits in every language, as the store's prices are printed */
  function intlLang() { var l = GCX.lang(); return l.indexOf('-u-') === -1 ? l + '-u-nu-latn' : l; }
  GCX.num = function (n, opt) {
    try { return new Intl.NumberFormat(intlLang(), opt || { maximumFractionDigits: 1 }).format(n); } catch (e) { return String(n); }
  };
  GCX.pct = function (n, opt) {
    var o = { style: 'percent', maximumFractionDigits: 0 };
    if (opt) for (var k in opt) o[k] = opt[k];
    try { return new Intl.NumberFormat(intlLang(), o).format(Number(n) / 100); } catch (e) { return n + '%'; }
  };
  GCX.list = function (a, type) {
    a = (a || []).filter(function (x) { return x != null && x !== ''; }).map(String);
    try { return new Intl.ListFormat(GCX.lang(), { style: 'long', type: type || 'conjunction' }).format(a); }
    catch (e) { return a.join(', '); }
  };
  GCX.plural = function (n, forms) {
    if (!forms) return String(n);
    if (typeof forms === 'string') return GCX.tpl(forms, { n: GCX.num(n, { maximumFractionDigits: 0 }), count: GCX.num(n, { maximumFractionDigits: 0 }) });
    var cat = 'other';
    if (n === 0 && forms.zero != null) cat = 'zero';
    else { try { cat = new Intl.PluralRules(GCX.lang()).select(n); } catch (e) { cat = n === 1 ? 'one' : 'other'; } }
    var f = forms[cat] != null ? forms[cat] : forms.other;
    var nn = GCX.num(n, { maximumFractionDigits: 0 });
    return GCX.tpl(f == null ? String(n) : f, { n: nn, count: nn });
  };
  GCX.fromPrice = function (price, tpl) {
    var t = tpl || GCX.t('from', I18N_DEFAULT.from);
    if (t.indexOf('[price]') === -1 && t.indexOf('{price}') === -1) t = I18N_DEFAULT.from;
    return GCX.tpl(t, { price: typeof price === 'number' ? money(price) : String(price) });
  };
  GCX.slug = function (s) {
    if (s == null) return null;
    var v = String(s);
    try { v = v.normalize('NFKD').replace(/[\u0300-\u036f]/g, ''); } catch (e) {}
    v = v.toLowerCase();
    try { v = v.replace(/[^\p{L}\p{N}]+/gu, '-'); } catch (e) { v = v.replace(/[^a-z0-9]+/g, '-'); }
    v = v.replace(/^-+|-+$/g, '');
    return v || null;
  };

  /* ===================================================================
     1. Scroll rows with glass chevrons
     =================================================================== */
  var CHEV = {
    prev: '<svg viewBox="0 0 6 9" aria-hidden="true" focusable="false"><path d="M5 8.5l-4-4 4-4" stroke="currentColor" fill="none" stroke-linecap="square"/></svg>',
    next: '<svg viewBox="0 0 6 9" aria-hidden="true" focusable="false"><path d="M1 8.5l4-4-4-4" stroke="currentColor" fill="none" stroke-linecap="square"/></svg>'
  };
  function makeChev(dir, label, small) {
    var b = doc.createElement('button');
    b.type = 'button';
    b.className = 'gcx-chev gcx-chev--' + dir + (small ? ' gcx-chev--sm' : '');
    b.setAttribute('aria-label', label);
    b.innerHTML = CHEV[dir];
    return b;
  }
  function directChild(parent, cls) {
    for (var c = parent.firstElementChild; c; c = c.nextElementSibling) if (c.classList.contains(cls)) return c;
    return null;
  }

  /* ===================================================================
     1a. Row motion (kit 1.7.0): the one mover every kit row glides with
         (a chevron press, a mouse release, a glide row's coast), so every
         row on the pages moves the same way. Owner, 2 Oct 2026: a chevron
         "when clicked, smoothly swipe to the next, not the sudden one".
         - One curve: a cubic from the row's speed at that moment to a
           stop on the target. From rest it is a symmetric cubic ease-in-out
           (about 400-520 ms for a chevron press, chevMs). A press while the
           row still moves bends the same glide toward the new target: the
           position and the speed carry on unbroken (no restart from zero,
           no jolt), and presses in a row add up, one item each.
         - requestAnimationFrame and scrollLeft only: the compositor moves
           the row, nothing is laid out again (no CLS), and a finger keeps
           the browser's own swipe.
         - While it glides: snapping off and instant scrolling (inline,
           !important, so neither the snap points nor scroll-behavior fight
           the frames). The page's own values come back when it stops, on a
           point the row's own snapping would pick, so nothing moves after.
         - A finger, or a wheel that moves the row sideways (a trackpad), stops
           it at once, where it is (kit 1.7.1): snapping stays off while the
           person's own scrolling runs (the finger, its fling, the wheel and
           its momentum) and comes back only after the row has settled on a
           snap point in a short glide, so the browser never re-snaps it in
           one frame. An up-and-down wheel over a gliding row scrolls the
           page and leaves the glide alone.
         - A press the other way while it moves (kit 1.7.1) turns it back in
           the same curve at the speed it had, without a brake.
         - prefers-reduced-motion: it jumps there (no glide).
         - RTL: scrollLeft runs 0 .. -max in a track whose computed
           direction is rtl; "dir" +1 is always toward the track's end.
     =================================================================== */
  /* cubic Hermite from x0 (speed m0, in px per whole glide) to x1 (speed 0), k = 0..1 */
  function hermite(x0, x1, m0, k) {
    var k2 = k * k, k3 = k2 * k;
    return (2 * k3 - 3 * k2 + 1) * x0 + (k3 - 2 * k2 + k) * m0 + (3 * k2 - 2 * k3) * x1;
  }
  function hermiteD(x0, x1, m0, k) {
    var k2 = k * k;
    return (6 * k2 - 6 * k) * x0 + (3 * k2 - 4 * k + 1) * m0 + (6 * k - 6 * k2) * x1;
  }
  /* a chevron press: about 400 ms for a thumbnail's step, 440 ms for a card, at most 520 ms for a page */
  function chevMs(d) { return Math.round(Math.min(520, 380 + 30 * Math.log(1 + Math.abs(d) / 100) / Math.LN2)); }
  function nearestIn(list, x) {
    var best = null;
    list.forEach(function (p) { if (best === null || Math.abs(p - x) < Math.abs(best - x)) best = p; });
    return best;
  }
  function motion(row, track) {
    if (track.__gcxMotion) return track.__gcxMotion;
    var raf = 0, cur = null, lastT = 0, lastX = 0, saved = null, snapType = 'none', wait = null, wrote = 0;
    function isRtl() { return getComputedStyle(track).direction === 'rtl'; }
    function maxS() { return Math.max(0, track.scrollWidth - track.clientWidth); }
    function clampX(x) { var m = maxS(); return isRtl() ? Math.min(0, Math.max(-m, x)) : Math.min(m, Math.max(0, x)); }
    /* snap off and instant scrolling while a script or the mouse moves the row (inline, so no page rule outranks it) */
    function hold() {
      if (saved) return;
      snapType = getComputedStyle(track).scrollSnapType || 'none';
      saved = [track.style.getPropertyValue('scroll-snap-type'), track.style.getPropertyPriority('scroll-snap-type'),
        track.style.getPropertyValue('scroll-behavior'), track.style.getPropertyPriority('scroll-behavior')];
      track.style.setProperty('scroll-snap-type', 'none', 'important');
      track.style.setProperty('scroll-behavior', 'auto', 'important');
    }
    function letGo() {
      if (!saved) return;
      if (saved[0]) track.style.setProperty('scroll-snap-type', saved[0], saved[1]); else track.style.removeProperty('scroll-snap-type');
      if (saved[2]) track.style.setProperty('scroll-behavior', saved[2], saved[3]); else track.style.removeProperty('scroll-behavior');
      saved = null;
    }
    /* stop the glide where it is; snapping stays off (a mouse press that caught the row settles it on release) */
    function halt() {
      endWait();
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      cur = null;
      row.classList.remove('gcx-settling');
    }
    function stop() { halt(); letGo(); }
    function jump(to) { halt(); hold(); track.scrollLeft = clampX(to); letGo(); }
    function frame(now) {
      raf = 0;
      var c = cur;
      if (!c) return;
      /* kit 1.7.1: moved since the last frame by something else (a wheel or a finger the browser applied before its
         event got here): the person has taken over, never write over it */
      if (c.t0 !== null && Math.abs(track.scrollLeft - wrote) > 1.5) { if (snapping()) catchRow(false, 0); else stop(); return; }
      if (c.t0 === null) c.t0 = now;
      var k = Math.min(1, Math.max(0, (now - c.t0) / c.dur));
      var x = k < 1 ? hermite(c.x0, c.x1, c.m0, k) : c.x1;
      track.scrollLeft = x;
      wrote = track.scrollLeft;
      lastT = now; lastX = x;
      if (k < 1) raf = requestAnimationFrame(frame);
      else stop();
    }
    /* glide to `to` in about `dur` ms; v0 = the speed to start at (px/ms, scroll units), else the row's own */
    function run(to, dur, v0) {
      endWait();
      to = clampX(to);
      var moving = !!(cur && cur.t0 !== null);
      var x0 = moving ? lastX : track.scrollLeft;
      if (v0 == null) {
        v0 = 0;
        if (moving) v0 = hermiteD(cur.x0, cur.x1, cur.m0, Math.min(1, Math.max(0, (lastT - cur.t0) / cur.dur))) / cur.dur;
      }
      var d = to - x0;
      if (reduced() || Math.abs(d) < 0.5) { jump(to); return; }
      var m0 = v0 * dur, r = m0 / d;
      /* never past the target: a start speed of at most 3x the average (a faster row gets a shorter glide). A row
         heading the other way (kit 1.7.1) keeps the speed it has and turns back in the same curve, no brake: the
         glide gets shorter (not under 200 ms), so it runs on at most about a quarter of the way back before it
         turns; only where that would carry it past the row's end does it turn with the old short ease */
      if (r > 3) { dur = Math.max(120, 3 * d / v0); m0 = 3 * d; }
      else if (r < -1) {
        var tb = Math.max(200, Math.min(dur, -3 * d / v0)), fits = false;
        for (var n = 0; n < 4 && !fits; n++) {
          var mb = v0 * tb, xe = hermite(x0, to, mb, mb / (3 * mb - 6 * d));
          if (Math.abs(clampX(xe) - xe) < 0.5) fits = true; else tb *= 0.8;
        }
        if (fits) { dur = tb; m0 = v0 * tb; } else m0 = -d;
      }
      hold();
      row.classList.add('gcx-settling');
      cur = { x0: x0, x1: to, m0: m0, dur: dur, t0: moving ? lastT : null };
      if (!raf) raf = requestAnimationFrame(frame);
    }
    /* the scroll positions the track snaps to, from its children's scroll-snap-align (null: no snapping); the
       page's own scroll-snap-type, read before hold() switched it off */
    function snaps() {
      var cs = getComputedStyle(track), type = saved ? snapType : (cs.scrollSnapType || 'none');
      if (type === 'none' || type.indexOf('y') === 0 || type.indexOf('block') === 0) return null;
      var tr = track.getBoundingClientRect(), bl = track.clientLeft;
      var viewL = tr.left + bl + num(cs.scrollPaddingLeft, 0);
      var viewR = tr.left + bl + track.clientWidth - num(cs.scrollPaddingRight, 0);
      var sl = track.scrollLeft, r = isRtl(), pts = [], stop = false;
      for (var c = track.firstElementChild; c; c = c.nextElementSibling) {
        if (!c.offsetWidth) continue;
        var ccs = getComputedStyle(c), al = String(ccs.scrollSnapAlign || 'none').split(' ');
        var a = al[al.length - 1];
        if (a === 'none') continue;
        var cr = c.getBoundingClientRect(), ml = num(ccs.scrollMarginLeft, 0), mr = num(ccs.scrollMarginRight, 0), p;
        if (a === 'center') p = sl + (cr.left - ml + cr.right + mr) / 2 - (viewL + viewR) / 2;
        else if ((a === 'start') !== r) p = sl + cr.left - ml - viewL;
        else p = sl + cr.right + mr - viewR;
        pts.push(clampX(p));
        if (ccs.scrollSnapStop === 'always') stop = true;
      }
      if (!pts.length) return null;
      var mandatory = type.indexOf('mandatory') !== -1;
      if (!mandatory) { var m = maxS(); pts.push(r ? -m : 0, r ? 0 : m); }
      pts.sort(function (x, y) { return x - y; });
      return { pts: pts, mandatory: mandatory, stop: stop };
    }
    /* one chevron press: dir +1 toward the track's end. px = the row's step (data-gcx-step), mode = its value.
       With snapping: the snap point ahead that is nearest to one step on, as the browser's own scrollBy picks it
       ("item", or a px step a page measured for one item: the next item; scroll-snap-stop: always: the next snap
       point; a fraction or no step: about that far, on a snap point only near one, as proximity snapping does).
       Without snapping (a glide row): "item" is the next item's inline-start edge, measured now (items may differ
       in width). Counted from where the row is heading, so presses in a row add up. */
    function step(dir, px, mode) {
      var r = isRtl(), sgn = r ? -1 : 1, here = track.scrollLeft;
      var ref = (cur ? cur.x1 : here) * sgn;
      var one = mode === 'item' || parseFloat(mode) > 1;
      var want = ref + (px > 0 ? px : Math.max(track.clientWidth * 0.8, 120)) * dir;
      var to = null, s = snaps();
      if (s) {
        var pts = [];
        s.pts.forEach(function (p) { p *= sgn; if (dir > 0 ? p > ref + 1 : p < ref - 1) pts.push(p); });
        if (pts.length) {
          pts.sort(function (a, b) { return (a - b) * dir; });
          to = s.stop ? pts[0] : nearestIn(pts, want);
          if (!one && !s.mandatory && Math.abs(to - want) > track.clientWidth * 0.4) to = want;
        } else to = dir > 0 ? maxS() : 0;
      } else if (one) {
        var cs = getComputedStyle(track), pad = num(r ? cs.paddingRight : cs.paddingLeft, 0);
        var tr = track.getBoundingClientRect(), line = r ? tr.right - pad : tr.left + pad, at0 = here * sgn;
        for (var it = track.firstElementChild; it; it = it.nextElementSibling) {
          if (!it.offsetWidth) continue;
          var b = it.getBoundingClientRect(), at = at0 + (r ? line - b.right : b.left - line);
          if (dir > 0 ? (at > ref + 2 && (to === null || at < to)) : (at < ref - 2 && (to === null || at > to))) to = at;
        }
        if (to === null) to = dir > 0 ? maxS() : 0;
      } else to = want;
      to = clampX(to * sgn);
      var from = cur && cur.t0 !== null ? lastX : here;
      run(to, chevMs(to - from));
    }
    /* A finger, or a wheel that scrolls the row sideways (a trackpad, a tilt wheel, shift + wheel), takes over at
       once. Kit 1.7.1: in a row with snapping, the glide stops where it is but the snapping stays off for as long as
       the person's own scrolling runs (the finger, its fling, the wheel and its momentum); put back at once (1.7.0),
       the browser re-snapped the row in a single frame, up to 40 px and sometimes backward. When the row has been
       still for QUIET ms (or at the browser's scrollend once the finger is up), it settles on the snap point next in
       the direction of travel (the person's own: the wheel's, else their last move; else the glide's) in a short
       glide, and only then gets its snapping back, on that point, so nothing moves. Where their own scroll left it
       just past a snap point (a quarter of the step, 40 px at most), it settles back onto that one rather than a
       whole step on. Proximity snapping: only onto a
       point near enough for the browser to snap to (else it stays, as the browser leaves it). An up-and-down wheel
       over a gliding row scrolls the page and leaves the glide alone. A row without snapping (a glide row, 1d) just
       stops, as before. A finger whose touchend never comes (its element removed) is let go after TOUCH_LOST ms
       without a scroll. */
    var QUIET = 120, TOUCH_LOST = 3000;
    function snapping() {
      var t = saved ? snapType : (getComputedStyle(track).scrollSnapType || 'none');
      return t !== 'none' && t.indexOf('y') !== 0 && t.indexOf('block') !== 0;
    }
    function arm() { clearTimeout(wait.tm); wait.tm = setTimeout(settle, wait.touch ? TOUCH_LOST : QUIET); }
    function onLift(e) { if (!wait || (e.touches && e.touches.length)) return; wait.touch = false; arm(); }
    function endWait() {
      if (!wait) return;
      clearTimeout(wait.tm);
      if (wait.touched) { window.removeEventListener('touchend', onLift, true); window.removeEventListener('touchcancel', onLift, true); }
      wait = null;
    }
    function catchRow(touch, hint) {
      if (!wait) {
        /* the browser may have applied the person's scroll before the event got here: then that is the direction */
        var x = track.scrollLeft, moved = cur && cur.t0 !== null ? x - wrote : 0;
        var d = Math.abs(moved) > 1.5 ? moved : cur ? cur.x1 - x : 0;
        halt();
        wait = { dir: d > 0.5 ? 1 : d < -0.5 ? -1 : 0, ref: x, own: Math.abs(moved) > 1.5, touch: false, touched: false, tm: 0 };
      }
      if (hint) wait.dir = hint;
      if (touch) {
        if (!wait.touched) {
          window.addEventListener('touchend', onLift, { capture: true, passive: true });
          window.addEventListener('touchcancel', onLift, { capture: true, passive: true });
        }
        wait.touch = wait.touched = true;
      }
      arm();
    }
    function settle() {
      var w = wait;
      if (!w) return;
      endWait();
      /* a mouse drag took the row over meanwhile (1b): its release settles it */
      if (row.classList.contains('gcx-dragging')) return;
      var s = snaps(), x = track.scrollLeft, to = null;
      if (s) {
        var near = nearestIn(s.pts, x), cw = track.clientWidth, ahead = null, back = null;
        s.pts.forEach(function (p) {
          if (!w.dir) return;
          if ((p - x) * w.dir > -0.5) { if (ahead === null || Math.abs(p - x) < Math.abs(ahead - x)) ahead = p; }
          else if (back === null || Math.abs(p - x) < Math.abs(back - x)) back = p;
        });
        to = ahead !== null ? ahead : near;
        if (w.own && ahead !== null && back !== null && Math.abs(x - back) <= Math.min(40, Math.abs(ahead - back) / 4)) to = back;
        if (!s.mandatory && Math.abs(to - x) > cw * 0.4) to = Math.abs(near - x) <= cw * 0.4 ? near : null;
      }
      if (to === null) { letGo(); return; }
      /* from rest, 220-720 ms by distance: at most about 7 px a frame (60 Hz) up to 200 px */
      run(to, Math.min(720, Math.max(220, 3.6 * Math.abs(to - x))));
    }
    track.addEventListener('wheel', function (e) {
      if (!raf && !wait) return;
      if (!snapping()) { if (raf) stop(); return; }
      var dx = e.deltaX || (e.shiftKey ? e.deltaY : 0);
      if (dx) catchRow(false, dx > 0 ? 1 : -1);
    }, { passive: true });
    track.addEventListener('touchstart', function () {
      if (!raf && !wait) return;
      if (!snapping()) { if (raf) stop(); return; }
      catchRow(true, 0);
    }, { passive: true });
    track.addEventListener('scroll', function () {
      if (!wait) return;
      var x = track.scrollLeft, dx = x - wait.ref;
      if (Math.abs(dx) > 2) { wait.dir = dx > 0 ? 1 : -1; wait.ref = x; wait.own = true; }
      arm();
    }, { passive: true });
    track.addEventListener('scrollend', function () { if (wait && wait.touched && !wait.touch) { clearTimeout(wait.tm); settle(); } }, { passive: true });
    track.__gcxMotion = {
      run: run, step: step, stop: stop, halt: halt, jump: jump, hold: hold, letGo: letGo, snaps: snaps,
      clampX: clampX, maxS: maxS, rtl: isRtl,
      busy: function () { return !!raf; },
      held: function () { return !!saved; },
      caught: function () { return !!wait; },
      goal: function () { return cur ? cur.x1 : null; }
    };
    return track.__gcxMotion;
  }

  /* ===================================================================
     1b. Mouse drag for the scroll rows (kit 1.4.0)
         A mouse can grab any kit row that overflows and move it sideways,
         like a finger (the feel of the customer-stories rail,
         assets/gc-ugc-stories.js): the row follows the pointer 1:1 once the
         press has moved more than DRAG_MIN px sideways, and on release it
         carries on a little with the flick and settles on a snap point of
         the track (its own scroll-snap-type / -align / -stop, so a
         "mandatory" photo strip lands on a photo and one with
         scroll-snap-stop: always moves one photo at most).
         - Mouse only (pointerType "mouse"): touch and pen swipe natively,
           a trackpad scrolls with its wheel events; the keyboard, the
           chevrons and the tab stops are untouched.
         - A press that moved the row (more than DRAG_MIN px sideways), or
           one that only caught a row still gliding (a chevron press or a
           release settling), never clicks: the click that follows it is
           eaten before any page handler sees it (window, capture phase),
           so a drag never opens a tile, follows a link or plays / pauses a
           video. A press that never moved the row (a slip of a few px, or
           any distance up or down) still clicks (1.5.0). A press that
           caught a gliding row settles it from there on release.
         - No native image or link "ghost" and no text selection while
           dragging (dragstart cancelled, user-select off, CSS 6b).
         - Opt-out: data-gcx-drag="off" on the row or the track, for a row
           whose own script already drags it. A press on a form field, a
           model-viewer or anything inside [data-gcx-nodrag] is left alone.
         - Snap is off (inline) only while the row is held or gliding, and
           the page's own scroll-behavior comes back after it (1a).
     =================================================================== */
  var DRAG_MIN = 5;
  var DRAG_SKIP = 'input,textarea,select,option,[contenteditable]:not([contenteditable="false"]),model-viewer,[data-gcx-nodrag]';
  var eatClick = null;
  window.addEventListener('click', function (e) {
    var t = eatClick;
    /* only the mouse's own click: a script's el.click() (a page turning its gallery) goes through */
    if (!t || !e.isTrusted) return;
    eatClick = null;
    if (e.target && e.target.nodeType === 1 && t.contains(e.target)) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);
  function dragRow(row, track) {
    if (track.__gcxDrag) return;
    if (row.getAttribute('data-gcx-drag') === 'off' || track.getAttribute('data-gcx-drag') === 'off') return;
    track.__gcxDrag = true;
    row.classList.add('gcx-drag');
    var mo = motion(row, track), st = null;
    function target(proj, from) {
      var s = mo.snaps();
      if (!s) return mo.clampX(proj);
      var best = nearestIn(s.pts, proj);
      if (s.stop) {
        /* scroll-snap-stop: always: never past the snap point next to the one the drag started on */
        var home = nearestIn(s.pts, from), i = s.pts.indexOf(home);
        var lo = s.pts[Math.max(0, i - 1)], hi = s.pts[Math.min(s.pts.length - 1, i + 1)];
        best = Math.min(hi, Math.max(lo, best));
        if (Math.abs(proj - from) > 1 && best === home && Math.abs(proj - home) > track.clientWidth * 0.2) best = proj > home ? hi : lo;
      }
      /* proximity: settle on a snap point only when the release lands near one */
      if (!s.mandatory && Math.abs(best - proj) > track.clientWidth * 0.4) return mo.clampX(proj);
      return best;
    }
    /* after a release: an ease-out (cubic) to the resting point, 240-560 ms by distance */
    function glide(to) {
      var d = to - track.scrollLeft;
      if (Math.abs(d) < 1 || reduced()) { mo.jump(to); return; }
      var dur = Math.min(560, Math.max(240, Math.abs(d) * 1.1));
      mo.run(to, dur, 3 * d / dur);
    }
    function end(e) {
      if (!st || (e && e.pointerId !== st.id)) return;
      var s = st;
      st = null;
      doc.removeEventListener('selectstart', noSelect, true);
      /* a drag (the row followed the mouse: more than DRAG_MIN px sideways), or a press that only stopped a
         gliding row (as a finger stops a gliding list), is not a click; a press that never moved the row (a
         small slip, however far up or down) still clicks, as a native click would */
      if (s.on || s.caught) {
        eatClick = track;
        setTimeout(function () { if (eatClick === track) eatClick = null; }, 0);
      }
      /* a press that caught a gliding row without moving it: settle from where it was caught */
      if (!s.on) { if (mo.held()) glide(target(track.scrollLeft, track.scrollLeft)); return; }
      row.classList.remove('gcx-dragging');
      try { if (track.hasPointerCapture && track.hasPointerCapture(s.id)) track.releasePointerCapture(s.id); } catch (err) {}
      /* a flick carries on a little (the last ~100ms of movement, at most 500px), a pointer that rested before the
         release does not */
      var v = e && e.timeStamp - s.lt < 90 ? Math.max(-2.5, Math.min(2.5, s.v)) : 0;
      var cur = track.scrollLeft;
      glide(target(cur - v * 200, s.left));
    }
    function noSelect(e) { if (st && st.on) e.preventDefault(); }
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      if (e.target && e.target.closest && e.target.closest(DRAG_SKIP)) return;
      if (track.scrollWidth - track.clientWidth <= 2) return;
      var caught = mo.busy();
      if (caught) mo.halt();
      st = { id: e.pointerId, x: e.clientX, left: track.scrollLeft, on: false, v: 0, lx: e.clientX, lt: e.timeStamp, caught: caught };
      doc.addEventListener('selectstart', noSelect, true);
    });
    track.addEventListener('pointermove', function (e) {
      if (!st || e.pointerId !== st.id) return;
      if (!(e.buttons & 1)) { end(e); return; }
      var dx = e.clientX - st.x;
      if (!st.on) {
        if (Math.abs(dx) <= DRAG_MIN) return;
        st.on = true;
        mo.hold();
        row.classList.add('gcx-dragging');
        try { track.setPointerCapture(st.id); } catch (err) {}
        try { var sel = window.getSelection && window.getSelection(); if (sel && sel.rangeCount) sel.removeAllRanges(); } catch (err) {}
      }
      e.preventDefault();
      var dt = e.timeStamp - st.lt;
      if (dt > 0) {
        var inst = (e.clientX - st.lx) / dt;
        st.v = dt > 80 ? inst : st.v * 0.5 + inst * 0.5;
        st.lx = e.clientX; st.lt = e.timeStamp;
      }
      track.scrollLeft = st.left - dx;
    });
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('lostpointercapture', function (e) { if (st && st.on && e.pointerId === st.id) end(e); });
    /* no native drag of a photo or a link out of the row while the mouse is on it */
    track.addEventListener('dragstart', function (e) { if (st) e.preventDefault(); });
  }

  /* ===================================================================
     1d. Glide rows (kit 1.6.0): data-gcx-motion="glide" on [data-gcx-scroll]
         The feel of the personalized case's "Ladies & Gentlemen" row
         (owner, 29-30 Sep 2026: "smoother, like the ladies / gentlemen
         swipe"), one copy for every row that asks for it:
         - No snapping on any device (CSS 6c): a finger swipe is the
           browser's own scroll (it follows the finger 1:1 on the compositor
           and carries on with the browser's momentum), and the row stays
           where the swipe leaves it: nothing is pulled to a tile edge.
         - Mouse: the row follows the pointer 1:1 once the press has moved
           more than DRAG_MIN px sideways; on release it carries on with the
           flick and slows to a stop in one ease-out from the release speed
           (about 900 ms, never pulled back). A press on a gliding row just
           stops it. The click that ends a drag, or that only stopped a
           glide, is eaten (as 1b), so a drag never selects a tile, follows
           a button or plays a video.
         - Chevrons (and api.go): exactly one tile per press, the next
           tile's edge measured at click time (tiles may differ in width),
           in the same glide as every kit row (1a, kit 1.7.0); presses in a
           row add up.
         - A wheel (trackpad) or a finger stops a glide at once.
         - prefers-reduced-motion: the chevrons jump and a mouse release
           stops where it is; the row still follows the finger and the mouse.
         Replaces 1b for that row (its data-gcx-drag is not read).
     =================================================================== */
  function glideRow(row, track) {
    if (track.__gcxGlide) return track.__gcxGlide;
    var mo = motion(row, track), st = null;
    /* after a drag: carry on with the release speed (v, px/ms of scroll) and slow to a stop, one continuous curve
       (an ease-out whose starting speed is the release speed), so the row never jumps or turns back */
    function coast(v) {
      if (reduced() || Math.abs(v) < 0.05) { mo.letGo(); return; }
      var from = track.scrollLeft, to = mo.clampX(from + v * 300);
      if (Math.abs(to - from) < 1) { mo.letGo(); return; }
      mo.run(to, 900, v);
    }

    /* mouse drag (touch and pen scroll natively) */
    row.classList.add('gcx-drag');
    function noSelect(e) { if (st && st.on) e.preventDefault(); }
    function end(e) {
      if (!st || (e && e.pointerId !== st.id)) return;
      var s = st;
      st = null;
      doc.removeEventListener('selectstart', noSelect, true);
      if (s.on || s.caught) {
        eatClick = track;
        setTimeout(function () { if (eatClick === track) eatClick = null; }, 0);
      }
      if (!s.on) return;
      row.classList.remove('gcx-dragging');
      try { if (track.hasPointerCapture && track.hasPointerCapture(s.id)) track.releasePointerCapture(s.id); } catch (err) {}
      /* the release speed from the last ~100 ms of movement; a mouse that rested before letting go stops there */
      var p = s.pts, last = p[p.length - 1], v = 0;
      if (e && e.type !== 'pointercancel' && e.timeStamp - last[0] < 80 && p.length > 1 && last[0] > p[0][0]) v = (last[1] - p[0][1]) / (last[0] - p[0][0]);
      v = Math.max(-2.5, Math.min(2.5, v));
      coast(-v);
    }
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      if (e.target && e.target.closest && e.target.closest(DRAG_SKIP)) return;
      if (mo.maxS() <= 2) return;
      var caught = mo.busy();
      mo.stop();
      st = { id: e.pointerId, x: e.clientX, left: track.scrollLeft, on: false, caught: caught, pts: [[e.timeStamp, e.clientX]] };
      doc.addEventListener('selectstart', noSelect, true);
    });
    track.addEventListener('pointermove', function (e) {
      if (!st || e.pointerId !== st.id) return;
      if (!(e.buttons & 1)) { end(e); return; }
      var dx = e.clientX - st.x;
      if (!st.on) {
        if (Math.abs(dx) <= DRAG_MIN) return;
        st.on = true;
        mo.hold();
        row.classList.add('gcx-dragging');
        try { track.setPointerCapture(st.id); } catch (err) {}
        try { var sel = window.getSelection && window.getSelection(); if (sel && sel.rangeCount) sel.removeAllRanges(); } catch (err) {}
      }
      e.preventDefault();
      track.scrollLeft = st.left - dx;
      st.pts.push([e.timeStamp, e.clientX]);
      while (st.pts.length > 2 && e.timeStamp - st.pts[0][0] > 100) st.pts.shift();
    });
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener('lostpointercapture', function (e) { if (st && st.on && e.pointerId === st.id) end(e); });
    track.addEventListener('dragstart', function (e) { if (st) e.preventDefault(); });
    track.__gcxDrag = true;
    track.__gcxGlide = {
      tile: function (dir) { mo.step(dir, 0, 'item'); },
      to: function (x) { mo.run(x, chevMs(x - track.scrollLeft)); },
      stop: mo.stop
    };
    return track.__gcxGlide;
  }

  function setupRow(row) {
    if (row.__gcxRow) { row.__gcxRow.update(); return row.__gcxRow; }
    var track = row.querySelector('[data-gcx-track]') || row.querySelector('.gcx-scroll__track');
    if (!track) return null;
    var small = row.classList.contains('gcx-scroll--sm') || row.hasAttribute('data-gcx-chev-sm');
    var prev = directChild(row, 'gcx-chev--prev');
    var next = directChild(row, 'gcx-chev--next');
    if (!prev) { prev = makeChev('prev', row.getAttribute('data-gcx-prev-label') || GCX.t('prev', 'Previous'), small); row.insertBefore(prev, row.firstChild); }
    if (!next) { next = makeChev('next', row.getAttribute('data-gcx-next-label') || GCX.t('next', 'Next'), small); row.appendChild(next); }
    if (!track.id) track.id = 'gcx-track-' + (++uid);
    prev.setAttribute('aria-controls', track.id);
    next.setAttribute('aria-controls', track.id);

    var raf = 0;
    function schedule() { if (!raf) raf = requestAnimationFrame(update); }
    function rtl() { return getComputedStyle(track).direction === 'rtl'; }
    function gap() { return num(getComputedStyle(track).columnGap, 0) || num(getComputedStyle(track).gap, 0) || 0; }
    function firstItem() {
      for (var c = track.firstElementChild; c; c = c.nextElementSibling) if (c.offsetWidth) return c;
      return null;
    }
    function stepSize() {
      var mode = row.getAttribute('data-gcx-step') || '';
      if (mode === 'item') { var it = firstItem(); if (it) return it.getBoundingClientRect().width + gap(); }
      var f = parseFloat(mode);
      if (f > 0 && f <= 1) return track.clientWidth * f;
      if (f > 1) return f;
      return Math.max(track.clientWidth * 0.8, 120);
    }
    /* 1d: a glide row's chevrons glide one tile; every other row's chevrons glide one step (one item for
       data-gcx-step="item") to the snap point the row's own snapping would pick, in the same glide (1a, kit 1.7.0;
       it was the browser's own smooth scrollBy, 100-200 ms, which read as a jump) */
    var glide = row.getAttribute('data-gcx-motion') === 'glide' ? glideRow(row, track) : null;
    var mo = motion(row, track);
    function go(dir) {
      if (glide) { glide.tile(dir); return; }
      mo.step(dir, stepSize(), row.getAttribute('data-gcx-step') || '');
    }
    /* update() runs on every scroll frame: it writes to the DOM only when something changed (kit 1.6.0) */
    function flag(btn, on) {
      btn.classList.toggle('is-off', !on);
      var ti = on ? 0 : -1;
      if (btn.tabIndex !== ti) btn.tabIndex = ti;
      if (on) { if (btn.hasAttribute('aria-hidden')) btn.removeAttribute('aria-hidden'); }
      else if (btn.getAttribute('aria-hidden') !== 'true') btn.setAttribute('aria-hidden', 'true');
    }
    function update() {
      raf = 0;
      if (!row.isConnected) return;
      var max = track.scrollWidth - track.clientWidth;
      var over = max > 2;
      /* |scrollLeft| is the distance from the track's start in LTR and RTL
         alike (RTL scrollLeft runs 0 .. -max), so prev/next need no swap.
         In a track whose computed direction is rtl, the prev chevron sits at
         the inline start (right) and its glyph is mirrored (.gcx-rtl, CSS). */
      var p = Math.abs(track.scrollLeft);
      var canPrev = over && p > 2;
      var canNext = over && p < max - 2;
      row.classList.toggle('gcx-rtl', rtl());
      /* never strand keyboard focus on a chevron that is about to vanish */
      var a = doc.activeElement;
      if ((a === prev && !canPrev) || (a === next && !canNext)) {
        var other = a === prev ? (canNext ? next : null) : (canPrev ? prev : null);
        if (other) other.focus();
        else { if (!track.hasAttribute('tabindex')) track.tabIndex = -1; track.focus({ preventScroll: true }); }
      }
      if (ownStop && track.tabIndex !== (over ? 0 : -1)) track.tabIndex = over ? 0 : -1;
      row.classList.toggle('gcx-overflow', over);
      row.classList.toggle('gcx-can-prev', canPrev);
      row.classList.toggle('gcx-can-next', canNext);
      flag(prev, canPrev);
      flag(next, canNext);
    }
    prev.addEventListener('click', function () { go(-1); });
    next.addEventListener('click', function () { go(1); });
    track.addEventListener('scroll', schedule, { passive: true });
    /* a track with nothing focusable inside still has to be reachable by
       keyboard while it scrolls (axe scrollable-region-focusable); a tab
       stop only while it overflows. tabindex="-1" in the markup counts as
       "not set", any other author value is kept. */
    var ownStop = (!track.hasAttribute('tabindex') || track.getAttribute('tabindex') === '-1') &&
      !track.querySelector('a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])');
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(schedule);
      ro.observe(track);
      toArr(track.children).forEach(function (c) { ro.observe(c); });
      if ('MutationObserver' in window) {
        new MutationObserver(function (muts) {
          muts.forEach(function (m) { toArr(m.addedNodes).forEach(function (n) { if (n.nodeType === 1) ro.observe(n); }); });
          schedule();
        }).observe(track, { childList: true });
      }
    } else {
      window.addEventListener('resize', schedule, { passive: true });
      window.addEventListener('load', schedule);
    }
    toArr(track.querySelectorAll('img')).forEach(function (img) { if (!img.complete) img.addEventListener('load', schedule, { once: true }); });
    if (!glide) dragRow(row, track);
    row.classList.add('gcx-ready');
    var api = { update: schedule, track: track, prev: prev, next: next, go: go, glide: glide };
    row.__gcxRow = api;
    update();
    return api;
  }
  GCX.scrollRows = function (root) { return within(root || doc, '[data-gcx-scroll]').map(setupRow); };
  GCX.scrollRow = setupRow;
  /* GCX.glideTo(track, x) (kit 1.7.0): a page's own move of a kit row (the chosen thumbnail brought to the
     middle, a chosen swatch brought into view) in the same glide as the chevrons (1a), x in scroll units;
     reduced motion: at once. Returns false for a track that is not a kit row's (the page scrolls it itself). */
  GCX.glideTo = function (track, x) {
    var row = track && track.closest ? track.closest('[data-gcx-scroll]') : null;
    if (!row || !row.__gcxRow || row.__gcxRow.track !== track || !isFinite(x)) return false;
    motion(row, track).run(x, chevMs(x - track.scrollLeft));
    return true;
  };

  /* ===================================================================
     1c. Mouse drag for a one-picture stage (kit 1.5.0)
         GCX.dragPager(el, {
           go:     function (dir) {}  required: +1 shows the next picture, -1 the previous
           when:   function () {}     optional: false leaves the mouse alone (a stage
                                      that pages only in one of its modes)
           follow: element | function optional: what leans with the pointer while it
                                      is held (the picture shown), inside el
           min:    40                 optional: px sideways that turn the page
         }) -> { update() }           update(): re-read when() (after a mode change)
         For a gallery that shows one picture at a time and pages with its
         chevrons or a touch swipe (not a scroll row, see 1b; the Leather
         Set's photo strip is a row). A mouse press that moves more than
         `min` px, mostly sideways (|dx| > 1.4 |dy|, as a touch swipe),
         turns the page on release: a drag to the left shows the next
         picture, to the right the previous one (mirrored in RTL). A shorter
         drag springs back and changes nothing. While held, `follow` leans
         a third of the way with the pointer (none with reduced motion).
         - The click that ends a drag (more than DRAG_MIN px sideways) is
           eaten before any page handler (no zoom, no video play / pause);
           a press that did not move sideways still clicks.
         - Open hand while when() is true (.gcx-pager-on), closed hand while
           held (.gcx-pager-held); no image ghost, no text selection.
         - Mouse only: touch and pen keep the page's own swipe; the keyboard
           and the chevrons are untouched. A press on a button, a link, a
           form field, a model-viewer or anything in [data-gcx-nodrag] is
           left alone.
     =================================================================== */
  var PAGER_SKIP = 'a[href],button,label,summary,[role="button"],[role="radio"],[role="tab"],[role="slider"],[data-gcx-scroll],' + DRAG_SKIP;
  GCX.dragPager = function (el, o) {
    if (!el || el.nodeType !== 1 || !o || typeof o.go !== 'function') return null;
    if (el.__gcxPager) return el.__gcxPager;
    var min = o.min > 0 ? o.min : 40, st = null, spring = 0;
    function on() { try { return o.when ? !!o.when() : true; } catch (err) { return false; } }
    function update() { el.classList.toggle('gcx-pager-on', on()); }
    function follower() {
      var f = typeof o.follow === 'function' ? o.follow() : o.follow;
      return f && f.nodeType === 1 && f !== el && el.contains(f) ? f : null;
    }
    /* the lean uses the individual translate property (inline), so it adds to the picture's own transforms and
       transitions instead of replacing them; the inline value it found is put back when it lets go */
    function lean(s, x) { if (s.f) s.f.style.setProperty('translate', x ? x.toFixed(1) + 'px 0' : '0px 0'); }
    function restore(s) {
      if (!s.f) return;
      if (s.saved) s.f.style.setProperty('translate', s.saved[0], s.saved[1]); else s.f.style.removeProperty('translate');
    }
    function springBack(s, from) {
      if (!s.f) return;
      if (spring) cancelAnimationFrame(spring.id);
      var t0 = 0, rec = { s: s, id: 0 };
      spring = rec;
      function step(now) {
        if (!t0) t0 = now;
        var k = Math.min(1, (now - t0) / 220), e = 1 - Math.pow(1 - k, 3);
        if (k < 1) { lean(s, from * (1 - e)); rec.id = requestAnimationFrame(step); }
        else { restore(s); if (spring === rec) spring = 0; }
      }
      rec.id = requestAnimationFrame(step);
    }
    function stopSpring() { if (spring) { cancelAnimationFrame(spring.id); restore(spring.s); spring = 0; } }
    function noSelect(e) { if (st && st.on) e.preventDefault(); }
    function end(e) {
      if (!st || (e && e.pointerId !== st.id)) return;
      var s = st;
      st = null;
      doc.removeEventListener('selectstart', noSelect, true);
      if (!s.on) return;
      el.classList.remove('gcx-pager-held');
      try { if (el.hasPointerCapture && el.hasPointerCapture(s.id)) el.releasePointerCapture(s.id); } catch (err) {}
      /* a drag is not a click (no zoom, no video toggle) */
      eatClick = el;
      setTimeout(function () { if (eatClick === el) eatClick = null; }, 0);
      var dx = s.dx, dy = s.dy;
      var turn = e && e.type !== 'pointercancel' && Math.abs(dx) > min && Math.abs(dx) > Math.abs(dy) * 1.4;
      if (!turn) { springBack(s, s.lean); return; }
      restore(s);
      var dir = dx < 0 ? 1 : -1;
      if (getComputedStyle(el).direction === 'rtl') dir = -dir;
      try { o.go(dir); } catch (err) { if (window.console) console.error(err); }
      update();
    }
    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      if (e.target && e.target.closest && e.target.closest(PAGER_SKIP)) return;
      update();
      if (!on()) return;
      stopSpring();
      st = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, dy: 0, on: false, f: null, saved: null, lean: 0 };
      doc.addEventListener('selectstart', noSelect, true);
    });
    el.addEventListener('pointermove', function (e) {
      if (!st) { if (e.pointerType === 'mouse' && !e.buttons) update(); return; }
      if (e.pointerId !== st.id) return;
      if (!(e.buttons & 1)) { end(e); return; }
      st.dx = e.clientX - st.x; st.dy = e.clientY - st.y;
      if (!st.on) {
        if (Math.abs(st.dx) <= DRAG_MIN) return;
        st.on = true;
        el.classList.add('gcx-pager-held');
        try { el.setPointerCapture(st.id); } catch (err) {}
        try { var sel = window.getSelection && window.getSelection(); if (sel && sel.rangeCount) sel.removeAllRanges(); } catch (err) {}
        st.f = reduced() ? null : follower();
        if (st.f) {
          var v = st.f.style.getPropertyValue('translate');
          st.saved = v ? [v, st.f.style.getPropertyPriority('translate')] : null;
        }
      }
      e.preventDefault();
      /* a third of the way, and never more than a quarter of the stage */
      var cap = el.clientWidth * 0.25;
      st.lean = Math.max(-cap, Math.min(cap, st.dx / 3));
      lean(st, st.lean);
    });
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('lostpointercapture', function (e) { if (st && st.on && e.pointerId === st.id) end(e); });
    /* no native drag of the picture out of the stage while the mouse holds it */
    el.addEventListener('dragstart', function (e) { if (st) e.preventDefault(); });
    /* a mode button inside the stage may switch when() on or off */
    el.addEventListener('click', function () { setTimeout(update, 0); });
    el.classList.add('gcx-pager');
    update();
    var api = { update: update };
    el.__gcxPager = api;
    return api;
  };

  /* ===================================================================
     2. Typewriter placeholder for the initials field
        <input class="gcx-input" placeholder="Your initials"
               data-gcx-typewriter="AK,MRL,TJW,CDB">
        Types and deletes the examples until the shopper focuses or types
        in ANY initials field; then every typewriter stops for good on this
        page view. Reduced motion: static placeholder, no animation.
     =================================================================== */
  var twAll = [];
  var twStopped = false;
  function stopAllTypewriters() {
    twStopped = true;
    twAll.forEach(function (t) { t.stop(); });
  }
  GCX.typewriter = function (input, opts) {
    if (!input) return null;
    if (input.__gcxTw) return input.__gcxTw;
    opts = opts || {};
    var list = opts.examples || (input.getAttribute('data-gcx-typewriter') || '').split(',');
    list = list.map(function (s) { return String(s).trim().toUpperCase(); }).filter(Boolean);
    if (!list.length) list = ['AK', 'MRL', 'TJW', 'CDB'];
    var base = input.getAttribute('data-gcx-placeholder');
    if (base == null) base = input.getAttribute('placeholder') || '';
    var typeMs = num(opts.typeMs || input.getAttribute('data-gcx-type-ms'), 150);
    var delMs = num(opts.deleteMs || input.getAttribute('data-gcx-delete-ms'), 75);
    var holdMs = num(opts.holdMs || input.getAttribute('data-gcx-hold-ms'), 1400);
    var gapMs = num(opts.gapMs || input.getAttribute('data-gcx-gap-ms'), 420);
    /* start by deleting the static placeholder the page rendered, so the
       switch from "Your initials" to the first example reads as typing */
    var txt = base.length <= 16 ? base.toUpperCase() : '';
    var i = txt ? -1 : 0, phase = txt ? 'del' : 'type', timer = 0, paused = false, dead = false;
    var api = {
      stop: function () { finish(); },
      get running() { return !dead; }
    };
    input.__gcxTw = api;
    input.setAttribute('data-gcx-placeholder', base);

    function finish() {
      if (dead) return;
      dead = true;
      clearTimeout(timer); timer = 0;
      input.setAttribute('placeholder', base);
      if (io) io.disconnect();
    }
    function later(ms) { clearTimeout(timer); timer = setTimeout(tick, ms); }
    function tick() {
      timer = 0;
      if (dead) return;
      if (!input.isConnected) { finish(); return; }
      if (paused) return;
      if (doc.hidden) { later(800); return; }
      var word = list[i % list.length];
      if (phase === 'type') {
        txt = word.slice(0, txt.length + 1);
        input.setAttribute('placeholder', txt);
        if (txt === word) { phase = 'hold'; later(holdMs); }
        else later(typeMs + Math.round(Math.random() * 60));
      } else if (phase === 'hold') {
        phase = 'del'; later(delMs);
      } else {
        txt = txt.slice(0, -1);
        input.setAttribute('placeholder', txt);
        if (!txt) { phase = 'type'; i++; later(gapMs); }
        else later(delMs);
      }
    }
    function engage() { stopAllTypewriters(); }
    ['focus', 'input', 'pointerdown', 'keydown'].forEach(function (ev) { input.addEventListener(ev, engage); });

    var io = null;
    if (twStopped || reduced() || input.value) { finish(); return api; }
    twAll.push(api);
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          paused = !e.isIntersecting || doc.hidden;
          if (!paused && !timer && !dead) later(300);
        });
      });
      io.observe(input);
    }
    later(900);
    return api;
  };
  if (mqReduce) {
    var onReduce = function () { if (mqReduce.matches) stopAllTypewriters(); };
    if (mqReduce.addEventListener) mqReduce.addEventListener('change', onReduce);
    else if (mqReduce.addListener) mqReduce.addListener(onReduce);
  }

  /* ===================================================================
     3. Engraving preview
        GCX.clean(v)             -> A-Z 0-9, uppercase, max GCX.maxChars() (8)
        GCX.engrave(el, text[, anchor]) -> the cleaned text
        GCX.anchor(el, anchor)   -> apply "x,y,w,rot[,skew]" or {x,y,w,rot,skew}
     =================================================================== */
  /* Initials are stamped in Latin capitals and ASCII digits in every language.
     Full-width Latin (Japanese/Chinese IME) and the digits of other scripts
     (Arabic-Indic, Persian, Devanagari, Tamil) are mapped to A-Z / 0-9 first;
     anything else is dropped. GCX.cleanInfo(v) also says whether something
     was dropped, so the page can show its "Latin letters only" hint. */
  var DIGIT_ZERO = [0x0660, 0x06F0, 0x0966, 0x0BE6, 0xFF10];
  function asciiFold(v) {
    return String(v == null ? '' : v).replace(/[\uFF21-\uFF3A\uFF41-\uFF5A]/g, function (c) {
      return String.fromCharCode(c.charCodeAt(0) - 0xFEE0);
    }).replace(/[\u0660-\u0669\u06F0-\u06F9\u0966-\u096F\u0BE6-\u0BEF\uFF10-\uFF19]/g, function (c) {
      var code = c.charCodeAt(0);
      for (var i = 0; i < DIGIT_ZERO.length; i++) if (code >= DIGIT_ZERO[i] && code <= DIGIT_ZERO[i] + 9) return String(code - DIGIT_ZERO[i]);
      return c;
    });
  }
  /* The longest engraving on sale: the top of the personalization-fee ladder
     layout/theme.liquid publishes (window.GC_FEE_LADDER). 5 without it, which
     is what the store sold before the ladder ran to eight. */
  GCX.maxChars = function () {
    var L = window.GC_FEE_LADDER, m = 0;
    if (L && L.length) for (var i = 0; i < L.length; i++) if (+L[i].max > m) m = +L[i].max;
    return m || 5;
  };
  GCX.cleanInfo = function (v) {
    var up = asciiFold(v).toUpperCase();
    var kept = up.replace(/[^A-Z0-9]/g, '');
    var dropped = up.replace(/\s+/g, '').length !== kept.length;
    var max = GCX.maxChars();
    return { value: kept.slice(0, max), dropped: dropped, tooLong: kept.length > max };
  };
  GCX.clean = function (v) { return GCX.cleanInfo(v).value; };
  function parseAnchor(a) {
    if (!a) return null;
    if (typeof a === 'string') {
      var p = a.split(/[\s,;]+/).filter(Boolean).map(parseFloat);
      a = { x: p[0], y: p[1], w: p[2], rot: p[3], skew: p[4] };
    }
    return a;
  }
  GCX.anchor = function (el, anchor) {
    var host = el && (el.classList.contains('gcx-engrave') ? el : el.closest('.gcx-engrave')) || el;
    var a = parseAnchor(anchor);
    if (!host || !a) return;
    var s = host.style;
    if (isFinite(a.x)) s.setProperty('--gcx-x', a.x + '%');
    if (isFinite(a.y)) s.setProperty('--gcx-y', a.y + '%');
    if (isFinite(a.w)) s.setProperty('--gcx-w', a.w + '%');
    s.setProperty('--gcx-rot', (isFinite(a.rot) ? a.rot : 0) + 'deg');
    s.setProperty('--gcx-skew', (isFinite(a.skew) ? a.skew : 0) + 'deg');
    if (typeof anchor === 'string') host.setAttribute('data-gcx-anchor', anchor);
  };
  GCX.engrave = function (el, text, anchor) {
    if (!el) return '';
    var host = el.classList.contains('gcx-engrave') ? el : (el.closest('.gcx-engrave') || el);
    var t = el.classList.contains('gcx-engrave__text') ? el : host.querySelector('.gcx-engrave__text');
    var v = GCX.clean(text);
    if (anchor) GCX.anchor(host, anchor);
    if (t) { setText(t, v); t.setAttribute('data-text', v); }
    /* past six characters the letters step down by 6/n, so the longest name
       keeps to the width six letters take (.gcx-engrave__text reads it) */
    host.style.setProperty('--gcx-scale', v.length > 6 ? (6 / v.length).toFixed(3) : '1');
    host.classList.toggle('gcx-has-text', !!v);
    return v;
  };
  /* <input data-gcx-engrave="#preview, .gcx-thumb-engrave"> mirrors itself into
     those previews and fires 'gcx:initials' {value, length, price, variant} */
  function bindEngraveInput(input) {
    if (input.__gcxEng) return;
    input.__gcxEng = true;
    var hintTimer = 0;
    /* data-gcx-latin-msg="Only the letters A-Z ..." data-gcx-latin-hint="#hint":
       when characters were dropped, the hint shows the message for 3 s */
    function latinHint() {
      var msg = input.getAttribute('data-gcx-latin-msg');
      var sel = input.getAttribute('data-gcx-latin-hint');
      var el = null;
      try { el = sel ? doc.querySelector(sel) : null; } catch (e) {}
      if (!msg || !el) return;
      if (el.__gcxHintOrig == null) el.__gcxHintOrig = el.textContent;
      setText(el, msg);
      el.classList.add('gcx-hint-on');
      clearTimeout(hintTimer);
      hintTimer = setTimeout(function () { setText(el, el.__gcxHintOrig); el.__gcxHintOrig = null; el.classList.remove('gcx-hint-on'); }, 3000);
    }
    function run(fire) {
      var raw = input.value;
      var info = GCX.cleanInfo(raw);
      var v = info.value;
      if (info.dropped) latinHint();
      if (raw !== v) {
        var pos = input.selectionStart;
        input.value = v;
        try { if (pos != null) input.setSelectionRange(Math.min(pos, v.length), Math.min(pos, v.length)); } catch (e) {}
      }
      var sel = input.getAttribute('data-gcx-engrave');
      if (sel) { try { toArr(doc.querySelectorAll(sel)).forEach(function (el) { GCX.engrave(el, v); }); } catch (e) {} }
      if (fire) {
        input.dispatchEvent(new CustomEvent('gcx:initials', {
          bubbles: true,
          detail: {
            value: v,
            length: v.length,
            dropped: info.dropped,
            price: v && typeof window.GC_PRICE === 'function' ? window.GC_PRICE(v) : null,
            variant: v && typeof window.GC_VARIANT === 'function' ? window.GC_VARIANT(v) : null
          }
        }));
      }
    }
    input.addEventListener('input', function () { run(true); });
    if (input.value) run(false);
  }
  /* ?gcx_debug=1 : click a preview to read the anchor under the pointer */
  var DEBUG = /[?&]gcx_debug=1/.test(location.search);
  function debugEngrave(host) {
    if (!DEBUG || host.__gcxDbg) return;
    host.__gcxDbg = true;
    host.classList.add('gcx-debug');
    var t = host.querySelector('.gcx-engrave__text');
    if (t && !t.textContent) setText(t, 'ABC');
    host.addEventListener('click', function (e) {
      var r = host.getBoundingClientRect();
      var cs = getComputedStyle(host);
      var x = ((e.clientX - r.left) / r.width * 100).toFixed(1);
      var y = ((e.clientY - r.top) / r.height * 100).toFixed(1);
      var w = parseFloat(cs.getPropertyValue('--gcx-w')) || 38.8;
      var rot = parseFloat(cs.getPropertyValue('--gcx-rot')) || 0;
      var a = x + ',' + y + ',' + w + ',' + rot;
      GCX.anchor(host, a);
      if (window.console) console.log('[GCX] data-gcx-anchor="' + a + '"', host);
    });
  }

  /* ===================================================================
     4. 3D box: lazy model-viewer. Like the PDP's 3D viewer it turns on its
        own from load (model-viewer auto-rotate) with the hand "wiggle"
        interaction prompt and a grab cursor; unlike the PDP, the first real
        interaction stops the turning for good (this page view): a drag that
        turns it, a pinch or the wheel (both zoom it), the arrow / +/- keys on
        the focused box, or the +/- buttons. A finger that lands on the box and
        scrolls the page is NOT an interaction (touch-action pan-y): it neither
        stops the turning nor hides the hint. Reduced motion: no turning and no
        wiggle from the start; the glass "drag to turn" hint stays.
     =================================================================== */
  var mvRequested = false;
  function loadModelViewer(src) {
    if (mvRequested || !src) return;
    if (window.customElements && window.customElements.get('model-viewer')) { mvRequested = true; return; }
    mvRequested = true;
    var s = doc.createElement('script');
    s.type = 'module';
    s.src = src;
    doc.head.appendChild(s);
  }
  /* user: true when the shopper handled the box (the hint goes); false = reduced motion */
  function freeze(mv, box, user) {
    if (!mv) return;
    mv.removeAttribute('auto-rotate');
    try { mv.autoRotate = false; } catch (e) {}
    mv.setAttribute('interaction-prompt', 'none');
    if (box) box.classList.add(user === false ? 'gcx-box3d--calm' : 'gcx-box3d--still');
  }
  function freezeAll() {
    GCX.__still = true;
    toArr(doc.querySelectorAll('[data-gcx-box3d]')).forEach(function (b) { freeze(b.querySelector('model-viewer'), b, true); });
  }
  function orbitRadius(mv, attr) {
    var v = mv.getAttribute(attr), r = v && v.split(/\s+/)[2];
    return r && r !== 'auto' ? parseFloat(r) : null;
  }
  function setupBox(box) {
    if (box.__gcxBox) return;
    var mv = box.querySelector('model-viewer');
    if (!mv) return;
    box.__gcxBox = true;
    var src = box.getAttribute('data-model-viewer-src');
    if (GCX.__still) freeze(mv, box, true);
    else if (reduced()) freeze(mv, box, false);

    /* camera per breakpoint; "auto" lets model-viewer frame the box to the card */
    var mqDesk = window.matchMedia('(min-width: 768px)');
    function cam() {
      var k = mqDesk.matches ? 'd' : 'm';
      var fov = box.getAttribute('data-gcx-fov-' + k), orbit = box.getAttribute('data-gcx-orbit-' + k);
      var min = box.getAttribute('data-gcx-min-' + k), max = box.getAttribute('data-gcx-max-' + k);
      if (fov) mv.setAttribute('field-of-view', fov);
      if (orbit) mv.setAttribute('camera-orbit', orbit);
      if (min) mv.setAttribute('min-camera-orbit', min);
      if (max) mv.setAttribute('max-camera-orbit', max);
    }
    cam();
    if (mqDesk.addEventListener) mqDesk.addEventListener('change', cam); else if (mqDesk.addListener) mqDesk.addListener(cam);

    /* what counts as handling the box (see the header): a mouse press, a finger that
       moves sideways (model-viewer turns the box; up / down scrolls the page), two
       fingers (pinch), the wheel, and the keys model-viewer answers to */
    var pts = {};
    mv.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') { if (e.button === 0) freezeAll(); return; }
      pts[e.pointerId] = [e.clientX, e.clientY];
      if (Object.keys(pts).length > 1) freezeAll();
    }, { passive: true });
    mv.addEventListener('pointermove', function (e) {
      var p = pts[e.pointerId];
      if (!p) return;
      var dx = Math.abs(e.clientX - p[0]), dy = Math.abs(e.clientY - p[1]);
      if (dx > 10 && dx > dy) freezeAll();
    }, { passive: true });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(function (ev) {
      mv.addEventListener(ev, function (e) { delete pts[e.pointerId]; }, { passive: true });
    });
    mv.addEventListener('wheel', freezeAll, { passive: true });
    mv.addEventListener('keydown', function (e) {
      if (/^(Arrow(Left|Right|Up|Down)|PageUp|PageDown|Home|End|\+|-|=|_)$/.test(e.key)) freezeAll();
    });
    mv.addEventListener('load', function () { box.classList.add('is-loaded'); });

    toArr(box.querySelectorAll('[data-gcx-zoom]')).forEach(function (btn) {
      btn.addEventListener('click', function () {
        freezeAll();
        if (typeof mv.getCameraOrbit !== 'function') return;
        var o = mv.getCameraOrbit(), step = num(box.getAttribute('data-gcx-zoom-step'), 0.12) * o.radius;
        var r = o.radius + (btn.getAttribute('data-gcx-zoom') === 'in' ? -step : step);
        var lo = orbitRadius(mv, 'min-camera-orbit'), hi = orbitRadius(mv, 'max-camera-orbit');
        if (lo != null) r = Math.max(lo, r);
        if (hi != null) r = Math.min(hi, r);
        mv.cameraOrbit = o.theta + 'rad ' + o.phi + 'rad ' + r.toFixed(4) + 'm';
      });
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { loadModelViewer(src); io.disconnect(); } });
      }, { rootMargin: '300px 0px' });
      io.observe(box);
    } else {
      loadModelViewer(src);
    }
  }
  GCX.box3d = function (root) { within(root || doc, '[data-gcx-box3d]').forEach(setupBox); };
  GCX.stop3d = freezeAll;

  /* ===================================================================
     5. Sticky Add to Cart: arming (main-product.liquid 359-378) and the
        label-pill notch width (393-407), plus GCX.setSticky().
     =================================================================== */
  (function () {
    /* verbatim logic; only change: the CTA is looked up again if the section
       holding it was re-rendered, and a CTA outside .Product__Info still counts */
    function atcEl() {
      return doc.querySelector('.Product__Info .ProductForm__AddToCart') || doc.querySelector('.ProductForm__AddToCart');
    }
    function boot() {
      var ticking = false;
      function apply() {
        ticking = false;
        var wrap = doc.querySelector('.cust-sticky-btn-container');
        var atc = atcEl();
        if (!wrap || !atc) return;
        /* 70px clears the sticky header */
        wrap.classList.toggle('gc-sticky-armed', atc.getBoundingClientRect().bottom < 70);
      }
      function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(apply); } }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      GCX.__armSticky = onScroll;
      apply();
      nowBar();
    }
    /* The bar of snippets/gcx-sticky (the leather set and the personalized case) shows from the first paint on
       phones (gc-sticky-now, owner 9 Oct 2026). It steps aside (gc-aside, assets/gc-pdp-main.css) so it never covers
       what the shopper is choosing, reading or typing:
       - one of the page's own pickers is open (any control with aria-haspopup and aria-expanded="true": the device
         list, the color pickers; and the initials field on the picture);
       - a "?" note is open (is-open), or either page's check before adding (its veil);
       an open control counts while it is on screen; a popup that sits apart from it (a list named by aria-controls,
       a note's bubble, the leather set's color popover) counts while it reaches the bar's place at the bottom. So a
       note or picker left open and scrolled away does not keep the bar hidden (9 Oct 2026);
       - on the leather set, its price under the title is on the first screen and still in the bar's place (owner,
         9 Oct 2026: the bar shows after a small scroll there, so the first screen shows the price clean);
       - a text field has the keyboard up. Android leaves the focus in the field when the keyboard is closed with
         Back, so the field stops counting once the screen grows back (kb 2), until it is tapped or focused again.
         The screen's full height is kept per width (never one measured with the keyboard up, prov), so a rotation
         with the keyboard up is not taken for Back, and after a rotation the screen growing by more than a keyboard
         over its lowest since (low, also in steps) is;
       and while Shopify's cookie banner is up (gc-consent-open, as sections/main-product.liquid does for the
       standard product pages). */
    function nowBar() {
      var wrap = doc.querySelector('.cust-sticky-btn-container[data-gcx-sticky-now]');
      if (!wrap) return;
      var OPEN = '[aria-haspopup][aria-expanded="true"], [data-gcls-spot][aria-expanded="true"], [data-gcpx-spot][aria-expanded="true"], .gc-sp-addon__tip.is-open, .gcls-veil.is-on, .gcpx-veil.is-on';
      var POPS = '.gc-sp-addon__tip.is-open .gc-sp-addon__tipbubble, [data-gcls-cpop]:not([hidden])';
      var PRICE = doc.querySelector('[data-gcls-pricelist]');
      var NOTYPE = /^(button|submit|reset|checkbox|radio|range|color|file|image|hidden)$/i;
      var talls = {}, vv = window.visualViewport, kb = 0, tall = 0, wide = 0, low = 0, prov = false, t = 0;
      function field(a) {
        return !!a && a !== doc.body && !wrap.contains(a) &&
          (a.tagName === 'TEXTAREA' || !!a.isContentEditable || (a.tagName === 'INPUT' && !NOTYPE.test(a.type || '')));
      }
      function band(n) {
        var H = window.innerHeight, p = n.getBoundingClientRect();
        return !!(p.width || p.height) && p.top < H && p.bottom > H - 90;
      }
      function inView(el) {
        var H = window.innerHeight, r = el.getBoundingClientRect();
        if ((r.width || r.height) && r.bottom > 0 && r.top < H) return true;
        var id = el.getAttribute('aria-controls'), pop = id && doc.getElementById(id);
        return !!pop && band(pop);
      }
      /* the first screen's height, as at load (re-taken when the width changes): Chrome's address bar sliding away
         makes innerHeight grow, which must not move the line between "first screen" and "below the fold" */
      var firstH = 0, firstW = 0;
      function priceHeld() {
        if (!PRICE) return false;
        var H = window.innerHeight, r = PRICE.getBoundingClientRect();
        if (window.innerWidth !== firstW) { firstW = window.innerWidth; firstH = doc.documentElement.clientHeight || H; }
        return r.height > 0 && r.bottom + window.pageYOffset <= firstH + 8 && r.bottom > H - 90;
      }
      function aside() {
        t = 0;
        var typing = kb !== 2 && field(doc.activeElement), open = priceHeld(), i;
        var els = doc.querySelectorAll(OPEN);
        for (i = 0; i < els.length && !open; i++) { if (!wrap.contains(els[i]) && inView(els[i])) open = true; }
        var pops = open ? [] : doc.querySelectorAll(POPS);
        for (i = 0; i < pops.length && !open; i++) { if (!wrap.contains(pops[i]) && band(pops[i])) open = true; }
        wrap.classList.toggle('gc-aside', typing || open);
      }
      function soon() { if (!t) t = setTimeout(aside, 0); }
      function vh() { return vv ? vv.height * (vv.scale || 1) : window.innerHeight; }
      function fresh() { kb = vh() < tall - 150 ? 1 : 0; low = vh(); soon(); }
      function resized() {
        var h = vh(), w = window.innerWidth, turn = w !== wide;
        if (turn) {
          if (!prov) talls[wide] = tall;
          wide = w; tall = talls[w] || 0;
          prov = !tall && (kb === 1 || (kb === 0 && field(doc.activeElement)));
          if (!tall && kb !== 2) kb = 0;
          low = h;
        }
        if (h > tall) tall = h;
        if (h < low) low = h;
        if (h < tall - 150) kb = 1;
        else if (kb === 1 || (kb === 0 && h - low > 150)) { kb = 2; prov = false; }
        soon();
      }
      doc.addEventListener('focusin', fresh);
      doc.addEventListener('focusout', soon);
      /* the notes open and close on a tap or a key; on window, in capture, so no page handler can stop it first */
      window.addEventListener('click', soon, true);
      window.addEventListener('keydown', soon, true);
      window.addEventListener('scroll', soon, { passive: true });
      window.addEventListener('load', soon);
      /* the price moving into or out of the bar's band, or appearing or going (two observers: one band, one screen) */
      if (PRICE && window.IntersectionObserver) {
        new IntersectionObserver(function () { soon(); }, { rootMargin: '0px 0px -90px 0px', threshold: [0, 0.5, 1] }).observe(PRICE);
        new IntersectionObserver(function () { soon(); }, { threshold: [0, 0.5, 1] }).observe(PRICE);
      }
      window.addEventListener('pointerdown', function (e) { if (e.target === doc.activeElement && field(e.target)) fresh(); }, true);
      (vv || window).addEventListener('resize', resized);
      resized();
      if (window.MutationObserver) new MutationObserver(soon).observe(doc.body, { attributes: true, subtree: true, attributeFilter: ['aria-expanded', 'aria-modal'] });
      aside();
      /* decided: assets/gc-pdp-main.css keeps the leather set's bar hidden until now, so it never flashes over the
         price under the title before the first decision (9 Oct 2026) */
      wrap.classList.add('gc-now-ready');

      function consent() {
        var b = doc.getElementById('shopify-pc__banner'), on = false;
        if (b) {
          var r = b.getBoundingClientRect(), cs = getComputedStyle(b);
          on = r.height > 0 && r.width > 0 && cs.display !== 'none' && cs.visibility !== 'hidden' && +cs.opacity !== 0;
        }
        wrap.classList.toggle('gc-consent-open', on);
      }
      consent();
      if (window.MutationObserver) {
        var seen = null, mo = null;
        new MutationObserver(function () {
          var b = doc.getElementById('shopify-pc__banner');
          if (b && b !== seen) {
            seen = b;
            if (mo) mo.disconnect();
            mo = new MutationObserver(consent);
            mo.observe(b, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
          }
          consent();
        }).observe(doc.body, { childList: true });
      }
    }
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();
  })();

  function sizePills() {
    toArr(doc.querySelectorAll('.product-upsell__pill, .gc-addons__label, .gcx-slab__label')).forEach(function (pill) {
      var panel = pill.closest('.product-upsell--drawer, .gc-addons, .gcx-slab');
      if (!panel) return;
      var w = Math.ceil(pill.getBoundingClientRect().width);
      if (w > 0) { panel.style.setProperty('--gc-pill-w', w + 'px'); panel.style.setProperty('--gcx-pill-w', w + 'px'); }
    });
  }
  GCX.sizePills = sizePills;
  window.addEventListener('load', sizePills);
  window.addEventListener('resize', sizePills, { passive: true });
  if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(sizePills);

  /* whole dollars print as "$89", never "$89.00"; an amount with cents keeps them (owner, 17 Oct 2026) */
  function money(cents) {
    var fmt = (window.theme && (window.theme.currencyCodeEnabled ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat)) || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return String(window.Shopify.formatMoney(cents, fmt)).replace(/[.,]00(?=\D*$)/, ''); } catch (e) {}
    }
    return '$' + (Number(cents) / 100).toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  GCX.money = money;
  /* snippets/gcx-price-chip: fill the amount and show the From words around it
     from the chip's own template (data-gcx-from-tpl), in the language's order */
  GCX.priceChip = function (chip, price, isFrom) {
    if (!chip) return;
    var tpl = chip.getAttribute('data-gcx-from-tpl') || GCX.t('from', I18N_DEFAULT.from);
    var parts = tpl.split(/\[price\]|\{price\}/);
    var pre = (parts[0] || '').trim(), post = (parts[1] || '').trim();
    var elPre = chip.querySelector('[data-gcx-chip-pre]'), elPost = chip.querySelector('[data-gcx-chip-post]');
    var elAmt = chip.querySelector('[data-gcx-chip-amt]');
    if (elAmt && price != null) setText(elAmt, priceText(price));
    if (elPre) { setText(elPre, pre); elPre.hidden = !isFrom || !pre; }
    if (elPost) { setText(elPost, post); elPost.hidden = !isFrom || !post; }
  };
  function priceText(p) { return typeof p === 'number' ? money(p) : String(p); }
  function readI18n(el) { try { return JSON.parse(el.getAttribute('data-i18n') || '{}'); } catch (e) { return {}; } }

  /* state: {price, compareAt, from, fromTpl, title, options, image, imageAlt,
             available, needsSelection, selectLabel, label, soldLabel}
     - every key optional */
  var stickyState = {};
  function applySticky(o) {
    var bar = doc.getElementById('sticky-add-to-cart-bar');
    var mBtn = doc.querySelector('.cust-sticky-btn');
    var i18n = bar ? readI18n(bar) : {};
    /* "From [price]": the page's template (setSticky fromTpl), else the bar's
       (data-gcx-from-tpl, from the section setting or the theme locale), so
       the word order follows the language ("$59.00から", "من $59.00") */
    var fromTpl = o.fromTpl || (bar && bar.getAttribute('data-gcx-from-tpl')) || '';
    var addLabel = o.label || i18n.addToCart || GCX.t('addToCart', 'Add to Cart');
    var soldLabel = o.soldLabel || i18n.soldOut || GCX.t('soldOut', 'Sold Out');
    var priceStr = o.price != null ? (o.from ? GCX.fromPrice(priceText(o.price), fromTpl) : priceText(o.price)) : null;

    if (bar) {
      if (o.title != null) setText(bar.querySelector('.StickyAddToCart__Title'), String(o.title));
      if (o.options != null) {
        var box = bar.querySelector('[data-sticky-options]');
        if (box) {
          var vals = Array.isArray(o.options) ? o.options : [o.options];
          vals = vals.filter(function (v) { return v != null && String(v) !== ''; });
          var html = vals.map(function (v, k) {
            var span = doc.createElement('span');
            span.textContent = String(v);
            return '<span class="StickyAddToCart__Option"><span class="StickyAddToCart__OptionValue">' + span.innerHTML + '</span></span>' +
              (k < vals.length - 1 ? '<span class="StickyAddToCart__OptionDivider">/</span>' : '');
          }).join('');
          if (box.innerHTML !== html) box.innerHTML = html;
          var sep = bar.querySelector('.StickyAddToCart__Separator');
          if (sep) sep.hidden = !vals.length;
        }
      }
      if (priceStr != null) setText(bar.querySelector('[data-sticky-price]'), priceStr);
      var cmp = bar.querySelector('[data-gcx-sticky-compare]');
      if (cmp && ('compareAt' in o)) {
        var showCmp = o.compareAt != null && o.compareAt !== false && o.compareAt !== 0 &&
          !(typeof o.compareAt === 'number' && typeof o.price === 'number' && o.compareAt <= o.price);
        cmp.hidden = !showCmp;
        if (showCmp) setText(cmp, priceText(o.compareAt));
      }
      var bBtn = bar.querySelector('[data-sticky-add-to-cart]');
      var bTxt = bar.querySelector('.StickyAddToCart__ButtonText');
      if (bBtn && ('available' in o || o.label)) {
        var off = o.available === false;
        bBtn.disabled = off;
        setText(bTxt, off ? soldLabel : addLabel);
        bBtn.setAttribute('aria-label', off ? soldLabel : addLabel);
      }
    }
    if (mBtn) {
      if (o.needsSelection != null) {
        mBtn.classList.toggle('needs-selection', !!o.needsSelection);
        /* gc-mobile-sticky.js only re-checks visibility on intersection changes */
        var wrap = mBtn.closest('.cust-sticky-btn-container');
        var atc = doc.querySelector('.Product__Info .ProductForm__AddToCart') || doc.querySelector('.ProductForm__AddToCart');
        if (wrap && atc) {
          var r = atc.getBoundingClientRect();
          var onScreen = r.bottom > 0 && r.top < window.innerHeight;
          wrap.classList.toggle('is-visible', !!o.needsSelection || !onScreen);
        }
      }
      if (o.selectLabel) setText(mBtn.querySelector('.StickySelectText'), o.selectLabel);
      var mTxt = mBtn.querySelector('.AddToCartText span') || mBtn.querySelector('.AddToCartText');
      if ('available' in o || o.label) setText(mTxt, o.available === false ? soldLabel : addLabel);
      if ('available' in o) mBtn.setAttribute('aria-disabled', o.available === false ? 'true' : 'false');
    }
    if ('image' in o) {
      toArr(doc.querySelectorAll('[data-gcx-sticky-img]')).forEach(function (img) {
        var src = o.image && (typeof o.image === 'string' ? o.image : o.image.src);
        if (src) {
          if (img.getAttribute('src') !== src) img.setAttribute('src', src);
          img.alt = o.imageAlt || '';
          img.hidden = false;
        } else {
          img.hidden = true;
        }
      });
    }
    if (GCX.__armSticky) GCX.__armSticky();
  }
  GCX.setSticky = function (o) {
    if (o) for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) stickyState[k] = o[k];
    var s = stickyState;
    applySticky(s);
    /* gc-sticky-atc.js rewrites the bar on variant:changed in the same tick;
       write once more on the next frame so the page's own values stay */
    requestAnimationFrame(function () { applySticky(s); });
    return s;
  };

  /* ===================================================================
     6. One-request add to cart (optional helper for both pages)
        GCX.cartAdd([{id, quantity, properties}, ...], opts) -> Promise(cart json)
        XMLHttpRequest with an {items:[...]} body, so personalize-drawer.js's
        fetch wrapper can never stamp a second personalization on it.
        opts.quiet (kit 1.6.0; GCX.cartAdd.quiet is true): the cart is only
        refreshed ('cart:refresh': theme.js re-renders the drawer's content
        and the header count, gc-cart-drawer.js recounts), with no
        'product:added' and no drawer (custom.js and theme.js open the drawer
        on product:added). For "Add to Cart & personalize another".
     =================================================================== */
  GCX.personalizationPair = function (caseVariantId, initials, qty) {
    var v = GCX.clean(initials);
    var q = qty || 1;
    if (!v) return [{ id: caseVariantId, quantity: q }];
    var fee = typeof window.GC_VARIANT === 'function' ? window.GC_VARIANT(v) : null;
    var link = 'p_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    var items = [{ id: caseVariantId, quantity: q, properties: { Personalization: v, _personalization_id: link } }];
    if (fee) items.push({ id: fee, quantity: q, properties: { For: v, _personalization_link: link } });
    return items;
  };
  GCX.cartAdd = function (items, opts) {
    opts = opts || {};
    /* this page's own flow never carries a drawer personalization */
    window.currentPersonalization = null;
    return new Promise(function (resolve, reject) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var x = new XMLHttpRequest();
      x.open('POST', root + 'cart/add.js');
      x.setRequestHeader('Content-Type', 'application/json');
      x.setRequestHeader('Accept', 'application/json');
      x.onload = function () {
        var data = null;
        try { data = JSON.parse(x.responseText); } catch (e) {}
        if (x.status >= 200 && x.status < 300) resolve(data); else reject(data || { status: x.status });
      };
      x.onerror = function () { reject({ status: 0 }); };
      x.send(JSON.stringify({ items: items }));
    }).then(function (data) {
      /* quantity: theme.js adds it to its item count (undefined made it NaN) */
      var qty = 0;
      items.forEach(function (it) { qty += Number(it && it.quantity) || 1; });
      if (!opts.quiet) doc.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { variant: null, items: items, quantity: qty } }));
      doc.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (opts.openDrawer !== false && !opts.quiet) {
        if (typeof window.refreshAndOpenCartDrawer === 'function') window.refreshAndOpenCartDrawer();
        else if (typeof window.openCartDrawer === 'function') window.openCartDrawer();
        else window.location.assign(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart');
      }
      return data;
    });
  };
  GCX.cartAdd.quiet = true;

  /* ===================================================================
     6b. In-page jumps without a link (kit 1.6.0; owner, 30 Sep 2026: no
         <a href="#..."> on these pages, for SEO). A <button type="button"
         data-gcx-jump="<element id>"> glides the page to that element (its
         own scroll-margin-top, else the sticky header's height + 12px) and
         moves the focus to it, or to its first h2 / h3 (tabindex -1 added
         when needed), without a second scroll, so the keyboard and a screen
         reader carry on from there. GCX.jump(id | element) does the same.
     =================================================================== */
  function headerBottom() {
    var h = doc.querySelector('.Header, #shopify-section-header header, header[role="banner"]');
    var r = h ? h.getBoundingClientRect() : null;
    if (r && r.bottom > 0 && r.bottom < 200) return r.bottom;
    return parseInt(getComputedStyle(doc.documentElement).getPropertyValue('--header-height'), 10) || 64;
  }
  GCX.jump = function (el) {
    if (typeof el === 'string') el = doc.getElementById(el);
    if (!el || !el.getClientRects().length) return false;
    var sm = num(getComputedStyle(el).scrollMarginTop, 0);
    var y = el.getBoundingClientRect().top + window.pageYOffset - (sm > 0 ? sm : headerBottom() + 12);
    try { window.scrollTo({ top: Math.max(0, y), behavior: reduced() ? 'auto' : 'smooth' }); } catch (e) { window.scrollTo(0, Math.max(0, y)); }
    var f = el.matches('h1,h2,h3') ? el : (el.querySelector('h2,h3') || el);
    if (!f.hasAttribute('tabindex')) f.setAttribute('tabindex', '-1');
    try { f.focus({ preventScroll: true }); } catch (e) {}
    return true;
  };
  doc.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-gcx-jump]');
    if (b && GCX.jump(b.getAttribute('data-gcx-jump'))) e.preventDefault();
  });
  /* (kit 1.6.1) a jump whose target is not on the page is hidden, with a wrapper that holds nothing else, so it
     never does nothing; a rating chip that jumps is described by the heading it jumps to, so what it is read as
     says where it goes ("Rated 4.7 out of 5 (405)" ... "Customer reviews") */
  function setupJump(b) {
    if (b.__gcxJump) return;
    b.__gcxJump = true;
    var t = doc.getElementById(b.getAttribute('data-gcx-jump'));
    if (!t) {
      b.hidden = true;
      var p = b.parentElement;
      if (p && p.children.length === 1 && p.textContent.trim() === b.textContent.trim()) p.hidden = true;
      return;
    }
    if (b.classList.contains('gcx-rating') && !b.hasAttribute('aria-describedby')) {
      var h = t.matches('h1,h2,h3') ? t : t.querySelector('h2,h3');
      if (h && h.textContent.trim()) {
        if (!h.id) h.id = 'gcx-jh-' + (++uid);
        b.setAttribute('aria-describedby', h.id);
      }
    }
  }

  /* ===================================================================
     6c. Still while unseen (kit 1.6.1, opt-in: GCX.calm()). An endless CSS
         animation keeps the browser making a new frame every 16 ms for as
         long as it runs, even far off screen, and one that is not
         composited (the delivery strip's pulsing dot is a box-shadow; the
         support tile's sway; a caret) redoes style, paint and commit on the
         main thread for each of them. On the personalized case that kept a
         4x-slowed phone's main thread ~95% busy while nothing moved on
         screen, so every tap waited (owner review, 30 Sep 2026: INP input
         delay). calm() pauses each endless animation while its element is
         off screen (150 px margin) and plays it again when the element comes
         back, so nothing a shopper can see changes. Animations that start
         later (a class added by another script) are picked up from their
         animationstart. Animations the page paused itself are left alone.
     =================================================================== */
  var calmIO = null;
  function endless(a) {
    try { return a.effect.getComputedTiming().iterations === Infinity; } catch (e) { return false; }
  }
  GCX.calm = function () {
    if (calmIO || typeof WeakMap !== 'function' || !doc.getAnimations || !('IntersectionObserver' in window)) return false;
    var byEl = new WeakMap();
    calmIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        (byEl.get(en.target) || []).forEach(function (a) {
          if (en.isIntersecting) {
            if (a.__gcxCalm && a.playState === 'paused') { a.__gcxCalm = false; try { a.play(); } catch (e) {} }
          } else if (a.playState === 'running') { a.__gcxCalm = true; try { a.pause(); } catch (e) {} }
        });
      });
    }, { rootMargin: '150px 0px 150px 0px' });
    function scan() {
      var all;
      try { all = doc.getAnimations(); } catch (e) { return; }
      all.forEach(function (a) {
        if (a.__gcxSeen || !endless(a)) return;
        var t = a.effect && a.effect.target;
        if (!t || t.nodeType !== 1) return;
        a.__gcxSeen = true;
        var list = (byEl.get(t) || []).filter(function (x) { return x.playState !== 'idle' && x.playState !== 'finished'; });
        list.push(a);
        byEl.set(t, list);
        /* (re)observing delivers a fresh entry, which applies the element's on-screen state to the new animation */
        calmIO.unobserve(t);
        calmIO.observe(t);
      });
    }
    var queued = 0;
    doc.addEventListener('animationstart', function () {
      if (!queued) queued = setTimeout(function () { queued = 0; scan(); }, 50);
    }, true);
    scan();
    return true;
  };

  /* ===================================================================
     7. Assurance helpers: the 3D row scrolls to the box; the support tile
        still works when gc-product-upsell.js (which owns it on the PDP)
        is not on the page.
     =================================================================== */
  function jump3d() {
    var t = doc.querySelector('[data-gcx-box3d]') || doc.querySelector(".Section[data-section-type='3d-model-viewer']");
    if (t) t.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'center' });
  }
  doc.addEventListener('click', function (e) {
    var row = e.target.closest && e.target.closest('.gcx-assurance #Product3DButton, [data-gcx-3d-jump]');
    if (row) { jump3d(); return; }
    var tile = e.target.closest && e.target.closest('.gcx-assurance [data-gc-support]');
    if (tile && typeof window.gcOpenChat !== 'function') {
      var href = tile.getAttribute('data-fallback');
      if (href) window.location.href = href;
    }
  });
  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var row = e.target.closest && e.target.closest('.gcx-assurance #Product3DButton, [data-gcx-3d-jump]');
    if (row && row.getAttribute('role') === 'button') { e.preventDefault(); jump3d(); }
  });
  window.addEventListener('load', function () {
    if (typeof window.gcOpenChat === 'function') return;
    toArr(doc.querySelectorAll('.gcx-assurance [data-gc-support-clip]')).forEach(function (clip) {
      /* plays while on screen whatever the reduced-motion setting, like the product videos (owner, 29 Sep 2026) */
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { var p = clip.play(); if (p && p.catch) p.catch(function () {}); } else clip.pause();
        });
      }, { threshold: 0.25 }).observe(clip);
    });
  });

  /* ===================================================================
     7b. Low stock (snippets/gcx-batch.liquid, kit 1.3.0). The PAGE decides
         what is true (its own variant flags from Liquid); the kit only shows it.
     =================================================================== */
  /* the low-stock line (kit 1.3.0: no base pill any more): shown only while
     `low` is true and the page printed a low text. Never a number. */
  GCX.batch = function (el, low) {
    if (!el) return false;
    low = !!low && !!el.querySelector('.gcx-batch__low');
    if (el.classList.contains('is-low') !== low) el.classList.toggle('is-low', low);
    var hide = el.classList.contains('gcx-batch--lowonly') ? !low : false;
    if (el.hidden !== hide) el.hidden = hide;
    return low;
  };
  /* the sold-out note: o = {show, text?, pick: {key, label, img, aria?} | null}.
     The button carries data-key; the page handles the tap (one tap = that color). */
  GCX.batchOut = function (el, o) {
    if (!el) return;
    o = o || {};
    var show = !!o.show;
    if (el.hidden === show) el.hidden = !show;
    if (!show) return;
    var t = el.querySelector('[data-gcx-batch-out-t]');
    if (t && o.text != null) { if (t.textContent !== o.text) t.textContent = o.text; t.hidden = !o.text; }
    var b = el.querySelector('[data-gcx-batch-pick]');
    if (!b) return;
    var pk = o.pick || null;
    b.hidden = !pk;
    if (!pk) { b.removeAttribute('data-key'); return; }
    b.setAttribute('data-key', pk.key);
    var bt = b.querySelector('[data-gcx-batch-pick-t]');
    if (bt && bt.textContent !== pk.label) bt.textContent = pk.label;
    if (pk.aria) b.setAttribute('aria-label', pk.aria); else b.removeAttribute('aria-label');
    var sw = b.querySelector('[data-gcx-batch-pick-img]');
    if (sw) {
      var bg = pk.img ? 'url("' + String(pk.img).replace(/["\\]/g, '') + '")' : '';
      if (sw.style.backgroundImage !== bg) sw.style.backgroundImage = bg;
      sw.hidden = !pk.img;
    }
  };

  /* ===================================================================
     9. Zoom: the PDP's full-screen PhotoSwipe (snippets/gcx-pswp)
        GCX.zoom(items, index, opts) -> the PhotoSwipe instance, or null when the
        page has no .pswp or the theme's PhotoSwipe (libs.min.js) is missing.
        items: [{src, w, h, msrc?, ini?, anchor?}]
        opts:  from (focus on close), bounds(i) -> element the picture grows from /
               shrinks to, close(i) (called as it starts closing, i = picture shown)
        Options and gestures are theme.js ProductImageZoom's (PDP); the kit adds a
        tap / click outside the picture (or, by touch, on a picture that is not
        zoomed in) closes, the "n / total" counter, keyboard use of the round
        buttons, focus back to opts.from, and engraved initials (item.ini, anchor)
        drawn in CSS on the picture, so they zoom with it.
     =================================================================== */
  var zoomNow = null;
  GCX.zoom = function (items, index, o) {
    o = o || {};
    var el = doc.querySelector('[data-gcx-pswp]') || doc.querySelector('.pswp');
    if (!el || typeof window.PhotoSwipe !== 'function' || !items || !items.length) return null;
    if (zoomNow) { try { zoomNow.close(); } catch (e) {} zoomNow = null; }
    /* out of every blurred / transformed panel, which would trap position: fixed */
    if (el.parentNode !== doc.body) doc.body.appendChild(el);
    var phone = window.matchMedia('(max-width: 640px)').matches, cap = phone ? 1200 : 1800;
    var list = items.map(function (it) {
      var w = num(it.w, 0) || 2048, h = num(it.h, 0) || w;
      var k = Math.max((h <= w ? w : h) / cap, 1);
      return { src: it.src, msrc: it.msrc || '', w: Math.floor(w / k), h: Math.floor(h / k), initialZoomLevel: 0.65, gcx: it };
    });
    var from = o.from || doc.activeElement;
    var ui = el.querySelector('.pswp__ui');
    var prevB = el.querySelector('.pswp__button--prev'), nextB = el.querySelector('.pswp__button--next');
    var count = el.querySelector('[data-gcx-pswp-count]');
    var lang = window.languages || {};
    var ps = new window.PhotoSwipe(el, false, list, {
      index: Math.max(0, Math.min(list.length - 1, index | 0)),
      showHideOpacity: true, showAnimationDuration: 500, loop: false, history: false,
      closeOnVerticalDrag: false, allowPanToNext: false, pinchToClose: false,
      errorMsg: '<p class="pswp__error-msg">' + (lang.productImageLoadingError || '') + '</p>',
      scaleMode: 'zoom',
      getDoubleTapZoom: function (isMouse, item) { return isMouse ? 1.6 : item.initialZoomLevel < 0.7 ? 1 : 1.33; },
      getThumbBoundsFn: function (i) {
        var t = o.bounds ? o.bounds(i) : null;
        if (!t || !t.getBoundingClientRect) return undefined;
        var r = t.getBoundingClientRect(), y = window.pageYOffset || doc.documentElement.scrollTop;
        return { x: r.left, y: r.top + y, w: r.width };
      }
    });
    zoomNow = ps;
    /* the theme's: re-measure only when the width changes (mobile toolbars) */
    var upd = ps.updateSize, lastW = null;
    ps.updateSize = function () { if (lastW === null || lastW !== window.innerWidth) upd.call(ps, true); lastW = window.innerWidth; };
    function sync() {
      var i = ps.getCurrentIndex(), n = list.length;
      if (prevB) { if (i === 0) prevB.setAttribute('disabled', 'disabled'); else prevB.removeAttribute('disabled'); }
      if (nextB) { if (i + 1 >= n) nextB.setAttribute('disabled', 'disabled'); else nextB.removeAttribute('disabled'); }
      if (count) { count.hidden = n < 2; setText(count, (i + 1) + ' / ' + n); }
    }
    /* engraved initials: a .gcx-engrave the size of the picture inside its zoom-wrap */
    var ro = 'ResizeObserver' in window ? new ResizeObserver(function (es) {
      es.forEach(function (e) { var ov = e.target.__gcxEng; if (ov) { ov.style.width = e.target.style.width; ov.style.height = e.target.style.height; } });
    }) : null;
    function engrave() {
      toArr(el.querySelectorAll('.pswp__zoom-wrap')).forEach(function (zw) {
        var im = zw.querySelector('.pswp__img:not(.pswp__img--placeholder)') || zw.querySelector('.pswp__img');
        if (!im) return;
        var it = null;
        for (var i = 0; i < list.length; i++) if (list[i].container === zw || (!it && im.getAttribute('src') === list[i].src)) it = list[i];
        var ov = zw.querySelector('.gcx-pswp__eng');
        var ini = it && it.gcx.ini ? GCX.clean(it.gcx.ini) : '';
        if (!ini) { if (ov) ov.parentNode.removeChild(ov); return; }
        if (!ov) {
          ov = doc.createElement('div');
          ov.className = 'gcx-engrave gcx-engrave--bare gcx-pswp__eng';
          ov.setAttribute('aria-hidden', 'true');
          ov.innerHTML = '<div class="gcx-engrave__mark"><span class="gcx-engrave__text"></span></div>';
          zw.appendChild(ov);
        }
        if (im.__gcxEng !== ov) { im.__gcxEng = ov; if (ro) ro.observe(im); }
        ov.style.width = im.style.width || (it.w + 'px');
        ov.style.height = im.style.height || (it.h + 'px');
        GCX.engrave(ov, ini, it.gcx.anchor || null);
      });
    }
    var mo = 'MutationObserver' in window ? new MutationObserver(function () { engrave(); }) : null;
    function act(b) {
      if (b.classList.contains('pswp__button--close')) ps.close();
      else if (b.classList.contains('pswp__button--prev')) ps.prev();
      else if (b.classList.contains('pswp__button--next')) ps.next();
    }
    function onTap(e) {
      var d = e.detail || {}, t = e.target, b = t && t.closest ? t.closest('.pswp__button') : null;
      if (b) { act(b); return; }
      var onImg = !!(t && t.classList && t.classList.contains('pswp__img'));
      if (d.pointerType === 'mouse') { if (onImg) ps.toggleDesktopZoom(d.releasePoint); else ps.close(); return; }
      var it = ps.currItem;
      if (!it || ps.getZoomLevel() <= it.initialZoomLevel + 0.01) ps.close();
    }
    /* Enter / Space on a focused round button (a pointer's tap arrives as pswpTap) */
    function onClick(e) {
      if (e.detail !== 0) return;
      var b = e.target && e.target.closest ? e.target.closest('.pswp__button') : null;
      if (b) { e.preventDefault(); act(b); }
    }
    el.addEventListener('pswpTap', onTap);
    el.addEventListener('click', onClick);
    ps.listen('beforeChange', sync);
    ps.listen('afterChange', function () { sync(); engrave(); });
    ps.listen('imageLoadComplete', engrave);
    ps.listen('doubleTap', function (pt) {
      var z = ps.currItem.initialZoomLevel;
      if (ps.getZoomLevel() !== z) ps.zoomTo(z, pt, 333); else ps.zoomTo(z < 0.7 ? 1 : 1.33, pt, 333);
    });
    ps.listen('initialZoomIn', function () { if (ui) ui.classList.remove('pswp__ui--hidden'); });
    ps.listen('initialZoomOut', function () { if (ui) ui.classList.add('pswp__ui--hidden'); });
    ps.listen('close', function () { if (o.close) { try { o.close(ps.getCurrentIndex()); } catch (e) {} } });
    ps.listen('destroy', function () {
      el.removeEventListener('pswpTap', onTap);
      el.removeEventListener('click', onClick);
      if (mo) mo.disconnect();
      if (ro) ro.disconnect();
      toArr(el.querySelectorAll('.gcx-pswp__eng')).forEach(function (n) { n.parentNode.removeChild(n); });
      if (zoomNow === ps) zoomNow = null;
      if (from && from.focus && doc.contains(from)) { try { from.focus({ preventScroll: true }); } catch (e) {} }
    });
    ps.init();
    sync();
    engrave();
    if (mo) mo.observe(el.querySelector('.pswp__container') || el, { childList: true, subtree: true });
    return ps;
  };
  GCX.zoomOpen = function () { return zoomNow; };

  /* ===================================================================
     8. Init: now, on theme-editor section loads, and for markup added later
     =================================================================== */
  var SEL = '[data-gcx-scroll],[data-gcx-typewriter],[data-gcx-box3d],.gcx-engrave,[data-gcx-engrave],.gcx-slab__label,[data-gcx-jump]';
  GCX.init = function (root) {
    root = root || doc;
    GCX.scrollRows(root);
    within(root, '[data-gcx-typewriter]').forEach(function (el) { GCX.typewriter(el); });
    within(root, '[data-gcx-box3d]').forEach(setupBox);
    within(root, '.gcx-engrave').forEach(function (el) {
      var a = el.getAttribute('data-gcx-anchor');
      if (a && !el.__gcxAnchored) { el.__gcxAnchored = true; GCX.anchor(el, a); }
      debugEngrave(el);
    });
    within(root, '[data-gcx-engrave]').forEach(bindEngraveInput);
    within(root, '[data-gcx-jump]').forEach(setupJump);
    sizePills();
  };
  function start() {
    GCX.init(doc);
    doc.addEventListener('shopify:section:load', function (e) { GCX.init(e.target); });
    if ('MutationObserver' in window) {
      var pending = [], scheduled = false;
      new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          toArr(m.addedNodes).forEach(function (n) {
            if (n.nodeType !== 1) return;
            if ((n.matches && n.matches(SEL)) || (n.querySelector && n.querySelector(SEL))) pending.push(n);
          });
        });
        if (pending.length && !scheduled) {
          scheduled = true;
          requestAnimationFrame(function () {
            scheduled = false;
            var list = pending; pending = [];
            list.forEach(function (n) { if (n.isConnected) GCX.init(n); });
          });
        }
      }).observe(doc.body || doc.documentElement, { childList: true, subtree: true });
    }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start); else start();
})();
